/**
 * RenderingStudio.jsx — Phase 4d (R3a live studio; §6.6 cleanup).
 *
 * Data sources are LIVE. Layout is the two-column tree/detail studio with a
 * LitGroup + Book picker strip and a collapsible activity log. The primary
 * "Render" button in the detail action bar is live via the `liveActions`
 * prop; per-character eye-test grids render inline in the detail pane via a
 * live <EyeTestGridPanel/> (bookId is threaded down for that).
 *
 * §6.6 changes:
 *   - Dropped the mock module import (MOCKUP_NOTICE / getActivityLog). Status
 *     constants moved to components/voiceRendering/renderingConstants.js.
 *   - bookId is now passed to <RenderUnitDetail/> so the in-tree eye-test
 *     grid can fetch/render for real.
 *   - The activity log renders empty until the server-side log endpoint
 *     (follow-up A9) lands — no fabricated entries.
 *   - The standalone legacy eye-test grid stays behind the header modal as a
 *     fallback until in-tree parity is confirmed in the field (A6).
 *
 * Data flow (live)
 * ----------------
 *   • Pickers — fetchLitGroupsDirect / fetchBooksByLitGroup (from
 *     userLitGroupAssetsSlice). Auto-selects the first owned LitGroup +
 *     book so the page never loads blank.
 *   • Tree — fetchRenderTree({ bookId }) + selectRenderTree(state,bookId).
 *     The slice TTL-caches at 30s; we force one revalidation per page entry.
 *   • Preflight + line-set — fetchRenderPreflight / fetchRenderedLines
 *     keyed by node.selector, adapted to the shapes the child components
 *     already consume.
 *   • Render action — startUnitRender({ bookId, selector }) via liveActions.
 *     A 409 preflightBlocked payload pops a Confirm; on confirm we re-dispatch
 *     with force:true. Polling deps are strictly [dispatch, jobId, inFlight].
 *
 * Cleanup
 * -------
 *   On bookId change & on unmount we dispatch clearUnitJob,
 *   clearPreflight({}), and clearRenderedLines({}). The render-tree
 *   cache is left intact (slice handles staleness).
 */

import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
// ⛔ In-app modal, never window.confirm — the no-native-dialogs rule.
import ConfirmModal from "../../../components/prototype/ConfirmModal";
import {
  Accordion,
  Button,
  Confirm,
  Container,
  Divider,
  Dropdown,
  Header,
  Icon,
  Label,
  Loader,
  Message,
  Modal,
  Popup,
  Segment,
} from "semantic-ui-react";

import RenderUnitTree from "../../../components/voiceRendering/RenderUnitTree";
import LineSplitModal from "../../../components/voiceRendering/LineSplitModal";
import LineJoinModal from "../../../components/voiceRendering/LineJoinModal";
import ChangeListPanel from "../../../components/voiceRendering/ChangeListPanel";
import RenderUnitDetail from "../../../components/voiceRendering/RenderUnitDetail";
import { decodeEntities } from "../../../utils/decodeEntities";
import CoverageReport from "../../../components/voiceRendering/CoverageReport";
import UnresolvedTextNotice from "../../../components/voiceRendering/UnresolvedTextNotice";
import { apiClient } from "../../../apiTools/apiClient";

import {
  fetchLitGroupsDirect,
  fetchBooksByLitGroup,
  selectDirectLitGroups,
  selectDirectLitGroupsStatus,
  selectDirectBooks,
  selectDirectBooksLitGroupUuid,
  selectDirectBooksStatus,
} from "../../../slices/userLitGroupAssetsSlice";

import {
  fetchRenderTree,
  fetchRenderPreflight,
  fetchRenderedLines,
  fetchStitchReadiness,
  startStitch,
  stopStitch,
  stopUnitRender,
  exportUnitZip,
  pollStitchProgress,
  resignRenderedChapterUrl,
  clearStitchJob,
  startUnitRender,
  pollUnitProgress,
  resignRenderedLineUrl,
  clearPreflight,
  clearRenderedLines,
  clearUnitJob,
  selectRenderTree,
  selectRenderTreeLoading,
  selectRenderTreeReview,
  selectPreflight,
  selectPreflightEntry,
  selectPreflightLoading,
  selectStitchReadiness,
  selectStitchReadinessLoading,
  selectStitchReadinessError,
  selectStitchJob,
  selectStitchJobLoading,
  selectStitchJobError,
  selectStitchJobInFlight,
  selectStitchJobBlocked,
  selectRenderedLineRows,
  selectRenderedLinesEntry,
  selectRenderedLinesLoading,
  selectUnitJob,
  selectUnitJobLoading,
  selectUnitJobInFlight,
  selectUnitJobPreflightBlocked,
} from "../../../slices/voiceRenderingSlice";

import RenderingStudioLegacyGrid from "./RenderingStudioLegacyGrid";
import AudioLabPanel from "./AudioLabPanel";
import DeliveryPanel from "./DeliveryPanel";
import PronunciationBench from "../../../components/voiceRendering/PronunciationBench";
import EditorialBench from "../../../components/voiceRendering/EditorialBench";
import AdjudicatorPanel from "../../../components/voiceRendering/AdjudicatorPanel";
import KindVoicePanel from "../../../components/voiceRendering/KindVoicePanel";
import { AuthStdFooter } from "../../../utils/auth-std-footer";
import ProcessGuide from "../../../components/help/ProcessGuide";
import "./RenderingStudio.css";

// Walk a render-unit tree once and flatten to { id → node } so the detail
// pane can resolve its selection without re-walking on every render.
const flattenTree = (node, map = {}) => {
  if (!node) return map;
  if (node.nodeId) map[node.nodeId] = node;
  (node.children || []).forEach((child) => flattenTree(child, map));
  return map;
};

// Container kinds don't emit lines; selecting one shouldn't fire a line-set
// fetch (the server returns empty rows + a `reason`, but skipping the call
// keeps the cache cleaner and avoids an empty 200 round-trip).
// NOTE: `frontMatter` (R3b Stage 2), `body` (R3b Stage 4), `bookPart` (R3b
// part nodes), `rearMatter` (closing credits) and `book` (book.full) are all
// executable line-emitting units, so they are NOT listed here — they must fetch
// their line-sets.
//
// `book` was held back "until R3c makes it expand". R3c did: book.full now walks
// opening credits → front matter → body → closing credits and returns 1,399 units
// for Ghost. This list was the only thing still saying otherwise, so selecting
// Whole Book skipped the fetch and showed an empty render list — the selector had
// the lines the whole time.
// Where the collapsed state of the render-unit tree is remembered. Per browser, per person — a
// workspace preference, so nothing else reads it and losing it costs one click.
const TREE_COLLAPSED_KEY = "rs.renderUnitTree.collapsed";

const NON_LINE_KINDS = new Set([
  "characterGroup",
  "characterIndex",
  "characterCrossBook",
]);
const isLineEmittingKind = (kind) => kind && !NON_LINE_KINDS.has(kind);

// Server preflight uses `blockers/warnings: [{code,message}]`;
// PreflightBanner renders strings. Flatten at the boundary so the banner
// component itself stays unchanged.
const adaptPreflight = (server) => {
  if (!server) return null;
  const flatten = (arr) =>
    (arr || []).map((b) =>
      typeof b === "string" ? b : b?.message || b?.code || String(b)
    );
  return {
    ...server,
    blockers: flatten(server.blockers),
    warnings: flatten(server.warnings),
  };
};

// Server rendered-line row → the line-set row shape RenderUnitDetail
// already consumes. Rendered lines carry `uuid` + `isActive:true`; the
// "rendered" marker is `uuid` (or `isActive`), NOT `signedUrl`.
const adaptLineRow = (row) => {
  const isRendered = !!(row.uuid || row.isActive);
  return {
    lineId: row.lineId,
    speaker: row.speaker,
    // What the Narrator is standing in FOR — "Sidenote" on a footnote, which names a kind of text
    // rather than a character and so could never be cast. Rendered as "Narrator (Sidenote)".
    //
    // ⚠️ This adapter copies fields EXPLICITLY, so anything not listed here is silently dropped on
    // the way to the table however correct the server is. That is exactly what happened when this
    // field was added: the API carried it, the table never saw it, and the column looked unchanged.
    speakerFallbackFrom: row.speakerFallbackFrom || null,
    // ⭐ That this line is SPOKEN DIFFERENTLY from how the book writes it — an editorial correction
    // is applied at render time and leaves the text alone, so without this the table shows the
    // author's words with nothing to say the audio differs. Listed here because of the warning
    // above: the same explicit-copy trap dropped contentKey between RenderJob and LineRenderer and
    // made every correction a silent no-op.
    editorial: row.editorial || null,
    // How many takes of this line exist, active + superseded. 1 on almost every line; >1 means an
    // earlier take survives and the row offers to restore it. Listed here for the reason above.
    takeCount: row.takeCount || 0,
    // ⭐ The emotion the EXISTING audio was rendered under. Compared with `emotion` to draw the amber
    // "the audio predates this label" mark — without it, changing a line's emotion looks applied while
    // the old take still plays, which is the same silent failure the editorial overlay had. Listed here
    // because of the warning above: this adapter copies explicitly, and this field would otherwise have
    // been the fourth to rediscover that.
    renderedEmotion: row.renderedEmotion || null,
    // ⭐ The audio tag this take was actually SENT with. Operator asked to see what reached ElevenLabs, and
    // nothing showed it: the row carries the book words, while the tag comes from the preset template. Sixth
    // field on this explicit-copy chain, listed for the reason the five above it are.
    renderedAudioTag: row.renderedAudioTag || null,
    // ⭐ Set only on a row produced by a line EDIT CHAIN — the anchor it replaced is not in the table at
    // all, so without this a part looks like a line the book has. Carries { anchorLineId, ordinal, kind,
    // of } so the row can mark itself and offer undo. Listed here because this adapter copies explicitly
    // and three fields before it were dropped in silence.
    editPart: row.editPart || null,
    // What KIND of thing this line is, and who lends that kind its voice. Both null on every
    // ordinary line. Listed here because this adapter copies explicitly — see the warning above,
    // which was written the last time a field the server sent never reached the table.
    utteranceKind: row.utteranceKind || null,
    kindVoice: row.kindVoice || null,
    // ⭐ Whose voice comes out of this line, when it is not the speaker's own — mimicry, an
    // impersonation, a recording of somebody. The speaker keeps the line; only the voice is borrowed,
    // so the table must show BOTH or an impression reads as a misattribution. Listed here for the
    // reason in the warning above, which this field would otherwise have rediscovered.
    spokenInVoiceOf: row.spokenInVoiceOf || null,
    // ⛔ Whether this line's own speaker is cast, which decides whether a KIND on it can ever be heard —
    // a kind lends its voice only to a character that has none. ⚠️ Copied with `??` and NOT `||`,
    // because `null` means "unknowable" and must stay distinct from `false` ("the speaker is uncast, a
    // kind will work"). `|| null` would collapse those two into one and turn a warning off silently.
    speakerHasOwnVoice: row.speakerHasOwnVoice ?? null,
    emotion: row.emotion,
    text: row.text,
    resolutionPath: row.resolutionPath || "unresolved",
    status: isRendered ? "rendered" : "not_rendered",
    durationMs: row.durationMs,
    renderedAt: row.renderedAt,
    // Only real chapter numbers become a "Ch.N" chip; front-matter rows carry
    // chapterNumber:null and should show no chapter chip (not "Ch.").
    chapter: typeof row.chapterNumber === "number" ? row.chapterNumber : undefined,
    signedUrl: row.signedUrl,
    uuid: row.uuid,
  };
};

const ActivityLog = ({ entries }) => {
  const [open, setOpen] = useState(false);
  return (
    <Accordion styled fluid className="rs-activity-log">
      <Accordion.Title active={open} onClick={() => setOpen((o) => !o)}>
        <Icon name="dropdown" />
        Activity log ({entries.length})
        <span className="rs-activity-log-hint">
          server-side log endpoint pending
        </span>
      </Accordion.Title>
      <Accordion.Content active={open}>
        {entries.length === 0 ? (
          <div className="rs-activity-empty">
            No activity yet — a server-side log stream is a follow-up.
          </div>
        ) : (
          <ul className="rs-activity-entries">
            {entries.map((e, i) => (
              <li key={i} className={`lvl-${e.level}`}>
                <span className="rs-activity-at">{e.at}</span>
                <span className="rs-activity-lvl">{e.level}</span>
                <span className="rs-activity-text">{e.text}</span>
              </li>
            ))}
          </ul>
        )}
      </Accordion.Content>
    </Accordion>
  );
};

const LegacyStudioModal = ({ open, onClose }) => (
  <Modal
    open={open}
    onClose={onClose}
    size="fullscreen"
    closeIcon
    className="rs-legacy-modal"
  >
    <Modal.Header>
      <Icon name="grid layout" />
      Standalone eye-test studio{" "}
      <Label color="grey" size="mini" style={{ marginLeft: 8 }}>
        FALLBACK
      </Label>
      <span className="rs-legacy-hint">
        the per-character eye-test now renders inline in the detail pane; this
        standalone view is kept as a fallback until in-tree parity is
        confirmed in the field
      </span>
    </Modal.Header>
    <Modal.Content scrolling>
      <RenderingStudioLegacyGrid />
    </Modal.Content>
    <Modal.Actions>
      <Button onClick={onClose}>
        <Icon name="arrow left" /> Back to Rendering Studio
      </Button>
    </Modal.Actions>
  </Modal>
);

const RenderingStudio = () => {
  const dispatch = useDispatch();

  // -------------------------------------------------------------------
  // Pickers (Chunk 1) — live LitGroup → Book.
  // -------------------------------------------------------------------
  const litGroups = useSelector(selectDirectLitGroups) || [];
  const litGroupsStatus = useSelector(selectDirectLitGroupsStatus);
  const books = useSelector(selectDirectBooks) || [];
  // Which group the loaded `books` actually belong to. During a group change these disagree, and
  // that window is where the stale-book bug lived.
  const booksLitGroupUuid = useSelector(selectDirectBooksLitGroupUuid);
  const booksStatus = useSelector(selectDirectBooksStatus);

  const [litGroup, setLitGroup] = useState(null);
  const [bookId, setBookId] = useState(null);

  // ⚠️ Declared AFTER `litGroup`, not beside the other selectors. `const` is in the temporal dead
  // zone until its declaration runs, so reading `litGroup` above this line is a ReferenceError at
  // render — the same shape as the boot-fatal PAUSE_REAR_SECTION_MS on 2026-08-20. The lint rule
  // `no-use-before-define` caught it; the build only warns, so nothing else would have.
  const booksAreForCurrentGroup = !!litGroup && booksLitGroupUuid === litGroup;

  // R3c stitch (Preflight + Stitch) — scoped to a selectable chunk of the book
  // (whole body, a chapter, or a part). Readiness/loading/error come from
  // voiceRenderingSlice (keyed by bookId, so they switch with the picker); the
  // scope + info message are local UI state.
  const [stitchMsg, setStitchMsg] = useState(null);
  // Default to the COMPLETE book. "book.body" was the old default and quietly drops the
  // credits and front matter — the widest-looking choice that is not the widest.
  const [stitchScope, setStitchScope] = useState("book.full");
  const stitchReadiness = useSelector((s) => selectStitchReadiness(s, bookId));
  const stitchBusy = useSelector((s) => selectStitchReadinessLoading(s, bookId));
  const stitchError = useSelector((s) => selectStitchReadinessError(s, bookId));

  const stitchJob = useSelector(selectStitchJob);
  const stitchJobInFlight = useSelector(selectStitchJobInFlight);
  const stitchJobLoading = useSelector(selectStitchJobLoading);
  const stitchJobError = useSelector(selectStitchJobError);
  const stitchBlocked = useSelector(selectStitchJobBlocked);

  const runPreflightStitch = useCallback(() => {
    if (!bookId) return;
    setStitchMsg(null);
    dispatch(fetchStitchReadiness({ bookId, selector: stitchScope }));
  }, [dispatch, bookId, stitchScope]);

  /**
   * ─── ⛔ AMBER IS NOT A BLOCKER, AND REQUIRING GREEN DEADLOCKED THE STITCH ──────────────────────
   *
   * This was `status === "green"`, and on 2026-09-27 that made the whole-book stitch unreachable for a
   * fully rendered book. Luck had 2,075 of 2,075 lines rendered and NO blockers — but one chapter
   * (ch33, test-stitched on the 19th and re-rendered on the 21st) raised the amber `stitch-stale`
   * warning, which downgrades green to amber, which disabled the button.
   *
   * ⭐ AND THE WARNING'S OWN REMEDY IS THE BUTTON IT DISABLED. `stitch-stale` says "Re-stitch to pick
   * it up", and `ChapterAssembler` supersedes any existing row and rebuilds every chapter in scope
   * from its active lines — so a whole-book stitch fixes the staleness as a side effect. The gate
   * blocked the only action that resolves what it was warning about, and the sole escape was "Stitch
   * to one MP3", which deliberately PERSISTS NOTHING and therefore could never clear it either.
   *
   * ⚠️ The rule now matches what the statuses mean: `red` carries blockers and stops the stitch;
   * `amber` carries warnings and is information to act on with open eyes. The scope check stays — a
   * verdict measured on another selector says nothing about this one.
   */
  const scopeReady =
    !!stitchReadiness &&
    stitchReadiness.status !== "red" &&
    stitchReadiness.selector === stitchScope;

  /** The amber reasons, for the button's tooltip — a warning the operator cannot read is noise. */
  const scopeWarnings = (stitchReadiness?.warnings || []).map((w) => w.message).filter(Boolean);

  /**
   * ⭐ `redo` REBUILDS CHAPTERS THAT LOOK CURRENT, and nothing could ask for it before.
   *
   * A chapter is "current" when its file is newer than its LINES — so a change to the stitch itself
   * (pause lengths, how gaps are generated) leaves all 44 looking up to date and the job answers
   * *"nothing to do — 44 chapter(s) already current"*. That is exactly right by its own rule and
   * useless when the code changed instead of the audio. Same shape as the Audio Lab's "Force all".
   */
  const runStitch = useCallback(({ redo = false } = {}) => {
    if (!bookId || !scopeReady) return;
    setStitchMsg(null);
    dispatch(startStitch({ bookId, selector: stitchScope, redo }));
  }, [dispatch, bookId, stitchScope, scopeReady]);

  // "Force re-stitch" re-encodes every chapter, so it asks first — see the button.
  const [confirmRestitch, setConfirmRestitch] = useState(false);


  // Reset scope + drop any prior stitch job/message when the book changes.
  useEffect(() => {
    setStitchMsg(null);
    // book.full, matching the initial state above — NOT book.body.
    //
    // This effect runs on mount and on every book change, so setting book.body here made it the
    // effective default and silently defeated the deliberate one twelve lines up. book.body drops
    // the opening credit, the front matter and the closing credit: the widest-LOOKING choice that
    // is not the widest. The Ghost of Madonna 13 is the book that proved it — rendered as
    // book.full (1,399 units), stitched as book.body (1,394), reported "39 chapters, 0 failed",
    // and produced a finished audiobook with no credits and a four-second chapter 0. See the
    // narrower-than-rendered warning in ChapterAssembler.
    setStitchScope("book.full");
    dispatch(clearStitchJob());
  }, [dispatch, bookId]);

  /**
   * Poll the stitch job while it assembles. Deps kept minimal (jobId + inFlight)
   * so the interval doesn't thrash on every snapshot mutation.
   *
   * ─── ⛔ A 404 IS PERMANENT, AND POLLING THROUGH IT NEVER ENDS ────────────────────────────────────
   *
   * Stitch jobs live in the SERVER'S MEMORY, so a restart erases them. The poll then 404s — "no such
   * job" — which is not a transient failure and can never recover: the job is gone for good. But the
   * interval kept firing every 1.5s regardless, so on 2026-09-27 the console filled with identical
   * 404s indefinitely after the server went down mid-stitch.
   *
   * ⚠️ AND THE WORK IS USUALLY FINE. That night the stitch had already finished — 80 of 80 chapters,
   * 2,075 lines, 14.72 hours — and all that was actually lost was the ability to WATCH it. So the
   * message must not read as failure: it says the job can no longer be tracked and points at the Audio
   * Lab, which reads `:RenderedChapter` from the graph precisely so a finished stitch outlives the
   * process that made it.
   */
  useEffect(() => {
    const jobId = stitchJob?.jobId;
    if (!jobId || !stitchJobInFlight) return undefined;
    const id = setInterval(async () => {
      const action = await dispatch(pollStitchProgress(jobId));
      if (action?.meta?.requestStatus === 'rejected' && action.payload?.status === 404) {
        clearInterval(id);
        dispatch(clearStitchJob());
        setStitchMsg(
          'The stitch job is no longer on the server — it was restarted while the job was running, '
          + 'and jobs are held in memory. Any chapters it had already written are safe: check the '
          + 'Audio Lab, which reads them from the database rather than from the job.'
        );
      }
    }, 1500);
    return () => clearInterval(id);
  }, [dispatch, stitchJob?.jobId, stitchJobInFlight]);

  // Stale-while-revalidate: always re-fetch the LitGroup list on mount so a
  // prior session/user's cached `directLitGroups` can never linger in the
  // picker (that stale cache was the cause of "wrong groups in the dropdown").
  // Any cached list stays visible during the refetch — the dropdown's
  // `loading` guard only spins when there is no data yet — so same-user
  // revisits show no flicker.
  useEffect(() => {
    dispatch(fetchLitGroupsDirect());
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dispatch]);

  // Auto-select the user's first owned LitGroup once available so the page
  // never loads blank. Also RE-seeds if a refetch (e.g. after a user switch)
  // no longer contains the previously-selected group.
  useEffect(() => {
    if (litGroups.length === 0) return;
    const ids = litGroups.map((lg) => lg.uuid || lg.value || lg.id);
    if (!litGroup || !ids.includes(litGroup)) {
      setLitGroup(ids[0]);
      setBookId(null);
    }
  }, [litGroup, litGroups]);


  // Fetch books whenever the LitGroup changes.
  useEffect(() => {
    if (litGroup) dispatch(fetchBooksByLitGroup(litGroup));
  }, [dispatch, litGroup]);

  // ⛔ CHANGING THE LITGROUP MUST FORCE AN EXPLICIT BOOK CHOICE.
  //
  // This used to auto-select `books[0]` whenever `bookId` was empty, and that is a RACE: choosing a
  // new group nulls `bookId` immediately, but `books` still holds the PREVIOUS group's list until
  // the fetch resolves — so the effect re-selected the OLD group's first book, and once `bookId`
  // was set again it never corrected itself when the real books arrived.
  //
  // The result was a Studio showing one group in the picker, a BLANK book box (the stale id is not
  // among the new options), and a render-unit tree still built from the stale book. On 2026-08-24
  // that produced a RED preflight naming a Variation belonging to a completely different LitGroup,
  // and cost an evening: every measurement was taken against the healthy book while the screen was
  // driving a different one.
  //
  // ⭐ SO THERE IS NO AUTO-SELECTION AT ALL. The book is always chosen by hand.
  //
  // "Never loads blank" was the original rationale, and it is the wrong trade for this page: every
  // action here — preflight, render, stitch, export — spends money against whichever book is
  // selected, so a book nobody chose is the last thing that should be sitting there armed. A page
  // that says "select a book" is not blank; it is honest about having nothing to act on.
  //
  // The LitGroup still auto-seeds (above), because choosing a group costs nothing and narrows the
  // list the operator has to read.

  const litGroupOptions = useMemo(
    () =>
      (litGroups || []).map((lg) => ({
        key: lg.uuid || lg.value || lg.id,
        value: lg.uuid || lg.value || lg.id,
        text: decodeEntities(lg.name || lg.text || lg.uuid),
      })),
    [litGroups]
  );

  const bookOptions = useMemo(
    () =>
      // ⛔ Offer NOTHING until the loaded list is known to belong to the selected group. Between
      // choosing a group and its books arriving, `books` still holds the previous group's list —
      // and a dropdown that offers another LitGroup's books is how you end up rendering one.
      (booksAreForCurrentGroup ? books || [] : [])
        // Never offer an archived (soft-deleted) book for rendering — the direct-books
        // list includes them (they're kept for restore), which showed the same title twice.
        .filter((b) => b.isActive !== false && b.status !== "Archived")
        .map((b) => ({
          key: b.uuid || b.value || b.id,
          value: b.uuid || b.value || b.id,
          text: decodeEntities(b.name || b.title || b.text || b.uuid),
        })),
    [books, booksAreForCurrentGroup]
  );

  // -------------------------------------------------------------------
  // Render-unit tree (Chunk 2) — fetched per-bookId, TTL-cached in slice.
  // -------------------------------------------------------------------
  const tree = useSelector((s) => selectRenderTree(s, bookId));
  const treeLoading = useSelector((s) => selectRenderTreeLoading(s, bookId));
  // Correctable text still outstanding — warned about BEFORE a long render, not discovered in it.
  const review = useSelector((s) => selectRenderTreeReview(s, bookId));
  const navigate = useNavigate();

  // Force the FIRST render-tree fetch after (re)entering the page so content
  // that changed while the operator was away (e.g. a VA recording or a render
  // job that finished elsewhere) is reflected on re-enter. Subsequent
  // in-session book switches keep using the slice's 30s TTL cache. The ref
  // resets on remount, so each page entry revalidates exactly once.
  const treeForcedRef = useRef(false);
  useEffect(() => {
    if (!bookId) return;
    dispatch(fetchRenderTree({ bookId, force: !treeForcedRef.current }));
    treeForcedRef.current = true;
  }, [dispatch, bookId]);


  const flat = useMemo(() => flattenTree(tree), [tree]);

  // Stitch-scope options — the stitchable chunks of the book: the whole body,
  // each chapter, and each book-part. Derived from the render tree so the
  // dropdown always matches what's actually in the book. `flat` walks the tree
  // depth-first, so body → chapters (in order) → parts comes out naturally.
  const stitchScopeOptions = useMemo(() => {
    // "book" (book.full) belongs here now that it expands: it is the ONLY scope that
    // carries the opening credits, the front matter and the closing credit. Without it the
    // dropdown's widest choice was "Whole body", which silently omits all five — a stitch
    // that looks complete, reports 39 of 39 chapters, and ships a book with no credits and
    // a four-second chapter 0.
    const STITCHABLE = new Set(["book", "body", "chapter", "bookPart"]);
    const opts = Object.values(flat)
      .filter((n) => n && STITCHABLE.has(n.kind) && n.selector)
      .map((n) => ({
        key: n.selector,
        value: n.selector,
        text: n.kind === "book"
          ? "Whole book (credits + front matter + all chapters)"
          : n.kind === "body" ? "Whole body (chapters only — no credits)"
            : n.label || n.selector,
      }))
      // Widest scope first, so the complete book is the one nearest the cursor.
      .sort((a, b) => (a.value === "book.full" ? -1 : b.value === "book.full" ? 1 : 0));
    // Guarantee a Body option even before the tree resolves.
    if (!opts.some((o) => o.value === "book.body")) {
      opts.unshift({
        key: "book.body",
        value: "book.body",
        text: "Whole body (all chapters)",
      });
    }
    return opts;
  }, [flat]);

  // --- Export: package the stitched chapters into one shareable zip -------------
  const [exporting, setExporting] = useState(false);
  const [exportResult, setExportResult] = useState(null);
  const [exportError, setExportError] = useState(null);
  const [copied, setCopied] = useState(false);

  // Label the zip with the scope's human name ("PART ONE ~ QUESTER") rather than the
  // selector, so what the listener downloads is named the way the book names itself.
  const stitchScopeLabel = useMemo(
    () => stitchScopeOptions.find((o) => o.value === stitchScope)?.text || stitchScope,
    [stitchScopeOptions, stitchScope]
  );

  const runExport = useCallback(async () => {
    if (!bookId) return;
    setExporting(true);
    setExportError(null);
    setExportResult(null);
    setCopied(false);
    try {
      const r = await dispatch(
        exportUnitZip({ bookId, selector: stitchScope, unitLabel: stitchScopeLabel })
      ).unwrap();
      setExportResult(r);
    } catch (e) {
      setExportError(e?.message || 'Export failed');
    } finally {
      setExporting(false);
    }
  }, [dispatch, bookId, stitchScope, stitchScopeLabel]);

  // Any scope change invalidates a previous export — the link points at a different unit.
  useEffect(() => {
    setExportResult(null);
    setExportError(null);
    setCopied(false);
  }, [stitchScope, bookId]);

  /**
   * If the current scope vanished (book switch, tree reshape), fall back to the widest scope there is.
   *
   * ⛔ TWO BUGS LIVED HERE, AND BOTH SET `book.body` BEHIND THE OPERATOR'S BACK.
   *
   * 1. **An empty option list is "not loaded yet", not "vanished".** `stitchScopeOptions` is derived
   *    from the render tree, which arrives asynchronously, so for the first render or two it is `[]`.
   *    This effect then found `book.full` missing and replaced it with `book.body` — defeating the
   *    deliberate default set twelve lines into the book-change effect above, which exists precisely
   *    to stop that. Visible as a flicker: the page drew "Whole book" and redrew "Whole body".
   *
   * 2. **The recovery preferred the narrower scope.** `book.body` drops the opening credit, the front
   *    matter and the closing credit — the widest-LOOKING choice that is not the widest. The Ghost of
   *    Madonna 13 shipped that way: rendered as `book.full` (1,399 units), stitched as `book.body`
   *    (1,394), reported "39 chapters, 0 failed", and arrived with no credits and a four-second
   *    chapter 0. A fallback that lands there contradicts the default it is supposed to protect.
   *
   * ⚠️ The genuine case still works: once the tree HAS loaded, a scope that really is gone gets
   * replaced — with `book.full` where the book has one, else `book.body`, else whatever exists, so
   * this can never leave an unselectable value behind.
   */
  useEffect(() => {
    if (!stitchScopeOptions.length) return;
    if (stitchScopeOptions.some((o) => o.value === stitchScope)) return;
    const has = (v) => stitchScopeOptions.some((o) => o.value === v);
    setStitchScope(
      has("book.full") ? "book.full"
        : has("book.body") ? "book.body"
          : stitchScopeOptions[0].value
    );
  }, [stitchScopeOptions, stitchScope]);

  // -------------------------------------------------------------------
  // Selection — selectedId (nodeId) is local; node.selector is the cache
  // key for live preflight + line-set lookups.
  // -------------------------------------------------------------------
  const [selectedId, setSelectedId] = useState(null);
  const selectedNode = selectedId ? flat[selectedId] || null : null;
  const selector = selectedNode?.selector || null;

  // Reset selection when bookId changes (nodeIds are not cross-book stable).
  useEffect(() => {
    setSelectedId(null);
  }, [bookId]);

  // -------------------------------------------------------------------
  // Preflight + line-set (Chunk 3) — keyed by node.selector.
  // -------------------------------------------------------------------
  const rawPreflight = useSelector((s) => selectPreflight(s, selector));
  const preflightLoading = useSelector((s) =>
    selectPreflightLoading(s, selector)
  );
  const rawLineRows = useSelector((s) => selectRenderedLineRows(s, selector));
  const lineRowsLoading = useSelector((s) =>
    selectRenderedLinesLoading(s, selector)
  );

  // Capture the kind for the line-emitting check; selectedNode is otherwise
  // unstable across re-selects.
  const selectedKind = selectedNode?.kind;

  /**
   * ─── ⭐ SELECTING A UNIT YOU HAVE ALREADY SEEN COSTS NOTHING ───────────────────────────────────
   *
   * Both of these are already cached per SELECTOR in the store (`preflight.bySelector`,
   * `renderedLines.bySelector`); the effect simply refetched on every selection change. That was free
   * while the preflight endpoint returned `{}` without waiting — it shipped a promise, so the response
   * was instant and the work was discarded. Now that it is awaited, each click pays for it.
   *
   * ⚠️ AND THE COST IS NOT THE WALK. Measured on The Luck of Madonna 13: `precheck` is 15 ms for the
   * whole book and under 1 ms for a chapter. `bootLinker` is ~996 ms — the roster plus `scriptJson`
   * from S3 — and BOTH requests boot one, so a click was paying about two seconds for a computation
   * that takes fifteen milliseconds. Skipping a request we do not need is therefore worth far more
   * than making the walk faster.
   *
   * ⛔ WHICH MAKES STALENESS THE THING TO GET RIGHT. A cached preflight is a claim about casting, and
   * casting changes in other panels — a merge, a reassignment, a voice attached in the Green Room.
   * Hence `refreshNonce`: the banner carries a recheck, and every action in this page that changes
   * resolution already refetches explicitly rather than relying on this effect.
   */
  const [refreshNonce, setRefreshNonce] = useState(0);
  const preflightEntry = useSelector((st) => selectPreflightEntry(st, selector));
  const linesEntry = useSelector((st) => selectRenderedLinesEntry(st, selector));

  useEffect(() => {
    if (!bookId || !selector) return;
    /**
     * ⛔ "DO WE HAVE IT" IS NOT ENOUGH — ASK ALSO "ARE WE ALREADY ASKING".
     *
     * Adding the cached entries to this effect's deps means it re-runs when the preflight arrives, and
     * an in-flight request has produced no ENTRY yet — so the line-set fetch was dispatched a second
     * time, which the browser console showed as a doubled GET for book.frontMatter. Two requests, each
     * booting a ~1s linker, to fetch one thing: the opposite of what the cache was added for.
     *
     * `entry` presence, not row COUNT: a unit with genuinely zero rows is a cached answer, and testing
     * emptiness would refetch it forever.
     */
    if (!preflightEntry && !preflightLoading) {
      dispatch(fetchRenderPreflight({ bookId, selector }));
    }
    if (isLineEmittingKind(selectedKind) && !linesEntry && !lineRowsLoading) {
      dispatch(fetchRenderedLines({ bookId, selector }));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dispatch, bookId, selector, selectedKind, refreshNonce, preflightEntry, linesEntry,
    preflightLoading, lineRowsLoading]);

  /** Recheck the current unit — the escape hatch for a cached reading that casting has moved past. */
  const refreshUnit = useCallback(() => {
    if (!bookId || !selector) return;
    dispatch(fetchRenderPreflight({ bookId, selector }));
    if (isLineEmittingKind(selectedKind)) dispatch(fetchRenderedLines({ bookId, selector }));
    setRefreshNonce((n) => n + 1);
  }, [dispatch, bookId, selector, selectedKind]);

  const preflight = useMemo(
    () => adaptPreflight(rawPreflight),
    [rawPreflight]
  );
  const lineSet = useMemo(
    () => (rawLineRows || []).map(adaptLineRow),
    [rawLineRows]
  );

  // -------------------------------------------------------------------
  // Render action (Chunk 4) — startUnitRender + pollUnitProgress.
  // 409 PREFLIGHT_BLOCKED → confirm dialog → re-dispatch with force=true.
  // -------------------------------------------------------------------
  const unitJob = useSelector(selectUnitJob);
  const unitJobInFlight = useSelector(selectUnitJobInFlight);
  const unitJobLoading = useSelector(selectUnitJobLoading);
  const unitJobBlocked = useSelector(selectUnitJobPreflightBlocked);

  const [confirmForce, setConfirmForce] = useState(false);
  const [confirmReRender, setConfirmReRender] = useState(false);
  // Lines the operator has ticked for a targeted re-render. A Set of lineId.
  const [selectedLineIds, setSelectedLineIds] = useState(() => new Set());
  const [confirmReRenderLines, setConfirmReRenderLines] = useState(false);
  const pendingForceRef = useRef(null);

  // Mirror 409 blocked into the confirm dialog. The slice stashes the
  // verdict on `unitJob.preflightBlocked` after a rejected start.
  useEffect(() => {
    if (unitJobBlocked && unitJobBlocked.selector && bookId) {
      pendingForceRef.current = {
        bookId,
        selector: unitJobBlocked.selector,
      };
      setConfirmForce(true);
    }
  }, [unitJobBlocked, bookId]);

  const handleStartRender = () => {
    if (!bookId || !selector) return;
    dispatch(startUnitRender({ bookId, selector }));
  };

  // RE-RENDER — the same call with `force:true`, which is what makes it different from Render.
  //
  // An ordinary Render consults the cache per line and returns the existing take for anything
  // already rendered, so re-clicking it CANNOT replace a bad take. `force` skips that check, which
  // is the only way to fix a line the model got wrong — and the reason a defective take used to be
  // unfixable from the Studio at all.
  //
  // It is confirmed because it is not free: force re-renders EVERY line in the unit, at TTS cost,
  // including the ones that were fine. The count is in the prompt so the spend is visible before
  // it happens rather than after.
  const handleReRender = () => {
    if (!bookId || !selector) return;
    setConfirmReRender(true);
  };

  const handleConfirmReRender = () => {
    setConfirmReRender(false);
    if (!bookId || !selector) return;
    dispatch(startUnitRender({ bookId, selector, force: true }));
  };

  // RE-RENDER SELECTED — the smallest repair the pipeline can do.
  //
  // Before this, the smallest repairable thing was a whole UNIT, so one dropped word cost a
  // chapter of TTS. The server takes `lineIds` alongside the selector and implies force for a
  // subset, so this handler stays honest by construction: it cannot accidentally ask for a
  // cache-hit that reports success having changed nothing.
  const handleReRenderLines = () => {
    if (!bookId || !selector || selectedLineIds.size === 0) return;
    setConfirmReRenderLines(true);
  };

  const handleConfirmReRenderLines = () => {
    setConfirmReRenderLines(false);
    if (!bookId || !selector || selectedLineIds.size === 0) return;
    dispatch(startUnitRender({ bookId, selector, lineIds: [...selectedLineIds] }));
    setSelectedLineIds(new Set());
  };

  /**
   * ─── DOWNLOAD THE SELECTED LINES AS MP3s ────────────────────────────────────────────────────
   *
   * ⭐ WHY IT IS FETCH-THEN-BLOB AND NOT AN <a download href={signedUrl}>. The `download`
   * attribute is IGNORED for a cross-origin href, so the direct link would save the file under
   * S3's key name — a uuid — and in some browsers would navigate instead of saving. Fetching the
   * bytes first makes the file same-origin as a blob, which is what lets it be named something a
   * person can read. The bucket's CORS already allows GET from this app's origins.
   *
   * ⚠️ A CACHED signedUrl EXPIRES. Playback learned this already and resigns on failure; the same
   * applies here, so a failed fetch is retried once through resignRenderedLineUrl rather than
   * reported as a broken line.
   *
   * ⓘ Sequential, not parallel: a burst of blob downloads is what makes a browser show its
   * "allow multiple downloads?" prompt, and ordering them also means the files land in the order
   * they were ticked. For dozens of lines a server-side zip would be the better answer — that is a
   * follow-up, not tonight's button.
   *
   * ⛔ Lines that were never rendered are SKIPPED, not failed: a tick on an unrendered line is an
   * ordinary thing to do (it is how you queue a re-render), and there is simply no audio to save.
   */
  const [downloadingLines, setDownloadingLines] = useState(false);
  const [downloadNote, setDownloadNote] = useState(null);

  const handleDownloadLines = async () => {
    if (selectedLineIds.size === 0 || downloadingLines) return;
    setDownloadingLines(true);
    setDownloadNote(null);

    const rows = lineSet.filter((r) => selectedLineIds.has(r.lineId));
    const rendered = rows.filter((r) => r.uuid);
    let saved = 0;
    const failed = [];

    const fetchBlob = async (url) => {
      const resp = await fetch(url);
      if (!resp.ok) throw new Error(`HTTP ${resp.status}`);
      return resp.blob();
    };

    for (const row of rendered) {
      try {
        let blob = null;
        if (row.signedUrl) {
          try { blob = await fetchBlob(row.signedUrl); } catch (_) { blob = null; }
        }
        if (!blob) {
          const fresh = await dispatch(resignRenderedLineUrl(row.uuid)).unwrap();
          if (!fresh?.signedUrl) throw new Error('resign returned no signedUrl');
          blob = await fetchBlob(fresh.signedUrl);
        }

        // A name that says what the audio IS. `Ch24_Glendyl_a1b2c3d4.mp3` can be found on a desk
        // three days later; the S3 key cannot.
        const safe = (s) => String(s || '').replace(/[^\w.-]+/g, '-').replace(/^-+|-+$/g, '').slice(0, 40);
        const parts = [
          typeof row.chapter === 'number' ? `Ch${row.chapter}` : null,
          safe(row.speaker) || 'line',
          String(row.lineId || '').slice(0, 8),
        ].filter(Boolean);

        const href = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = href;
        a.download = `${parts.join('_')}.mp3`;
        document.body.appendChild(a);
        a.click();
        a.remove();
        // ⚠️ Revoked on a tick, not immediately: revoking in the same frame as the click can cancel
        // the save in some browsers before the download has taken the reference.
        setTimeout(() => URL.revokeObjectURL(href), 10000);
        saved += 1;
      } catch (e) {
        failed.push(row.lineId);
      }
    }

    const skipped = rows.length - rendered.length;
    setDownloadingLines(false);
    setDownloadNote({
      saved,
      skipped,
      failed: failed.length,
      negative: failed.length > 0,
    });
  };

  /**
   * ─── ⭐ STITCH THE SELECTION INTO ONE MP3 (operator's request, 2026-09-19) ─────────────────────
   *
   * "Download MP3" saves each ticked line as its own file — right for keeping a take, wrong for
   * LISTENING to a passage: a scene audited as fourteen files is a scene nobody hears as a scene. This
   * asks the server to stitch the ticked lines with the chapter stitcher's own pause ladder, in BOOK
   * order whatever order they were ticked in, and streams back one file. Nothing is persisted.
   *
   * ⚠️ The receipt reports what was LEFT OUT (unrendered lines, or ticks belonging to another unit),
   * because a stitched file with a hole in it sounds like a finished passage.
   */
  const [stitchingLines, setStitchingLines] = useState(false);
  const [stitchLinesNote, setStitchLinesNote] = useState(null);
  const handleStitchLines = async () => {
    if (!bookId || !selector || selectedLineIds.size === 0 || stitchingLines) return;
    setStitchingLines(true);
    setStitchLinesNote(null);
    try {
      const r = await apiClient.post(`/voice-rendering/${bookId}/stitch-selection`,
        { selector, lineIds: Array.from(selectedLineIds) }, {}, { responseType: "blob" });
      const h = r.headers || {};
      const disp = String(h["content-disposition"] || "");
      const name = (disp.match(/filename="([^"]+)"/) || [])[1] || "selection.mp3";
      const href = URL.createObjectURL(r.data);
      const a = document.createElement("a");
      a.href = href;
      a.download = name;
      document.body.appendChild(a);
      a.click();
      a.remove();
      setTimeout(() => URL.revokeObjectURL(href), 10000);
      setStitchLinesNote({
        name,
        lines: Number(h["x-stitch-lines"] || 0),
        notRendered: Number(h["x-stitch-not-rendered"] || 0),
        notInUnit: Number(h["x-stitch-not-in-unit"] || 0),
        seconds: Math.round(Number(h["x-stitch-duration-ms"] || 0) / 1000),
      });
    } catch (err) {
      // With responseType "blob" the server's JSON error arrives as a Blob — read it back into words.
      let message = err.message || "Stitch failed";
      try {
        const text = await err.response?.data?.text?.();
        const parsed = text ? JSON.parse(text) : null;
        message = parsed?.error?.message || parsed?.error || message;
      } catch (_) { /* keep the generic message */ }
      setStitchLinesNote({ error: String(message) });
    } finally {
      setStitchingLines(false);
    }
  };

  const toggleLineSelected = (lineId) => {
    setSelectedLineIds((prev) => {
      const next = new Set(prev);
      if (next.has(lineId)) next.delete(lineId); else next.add(lineId);
      return next;
    });
  };

  // Select-all operates on what is SHOWING, not on the whole unit — it pairs with the detail
  // pane's text filter, so "search Madonna, tick all, re-render" is one gesture. That is exactly
  // the shape of the defect classes found by ear.
  const setLinesSelected = (lineIds, on) => {
    setSelectedLineIds((prev) => {
      const next = new Set(prev);
      lineIds.forEach((id) => (on ? next.add(id) : next.delete(id)));
      return next;
    });
  };

  const handleConfirmForce = () => {
    setConfirmForce(false);
    const pending = pendingForceRef.current;
    pendingForceRef.current = null;
    if (pending?.bookId && pending?.selector) {
      dispatch(
        startUnitRender({
          bookId: pending.bookId,
          selector: pending.selector,
          force: true,
        })
      );
    }
  };

  const handleCancelForce = () => {
    setConfirmForce(false);
    pendingForceRef.current = null;
    // Drop the blocked verdict so the next click of Render starts cleanly.
    dispatch(clearUnitJob());
  };

  // Polling — deps strictly [dispatch, jobId, inFlight]. Depending on the
  // whole `unitJob` object thrashes the interval (resume notes §3.4).
  useEffect(() => {
    const jobId = unitJob?.jobId;
    if (!jobId || !unitJobInFlight) return undefined;
    const id = setInterval(() => dispatch(pollUnitProgress(jobId)), 1500);
    return () => clearInterval(id);
  }, [dispatch, unitJob?.jobId, unitJobInFlight]);

  // -------------------------------------------------------------------
  // Patch A — auto-refresh preflight + line-set when a job *completes*.
  // Detect the inFlight false-edge (was true, now false) and refetch
  // for the job's selector (taken from unitJob.selector so a refresh
  // still lands even if the operator clicked away while it ran).
  //
  // §6.3-followup — also force-refetch the render TREE on this same edge so
  // the status pills (not_rendered / partial / complete) that now come from
  // real RenderedLine/RenderedGridCell counts repaint live, without waiting
  // for the slice's 30s TTL or a manual page re-enter. `force:true` bypasses
  // the TTL cache; the tree is keyed by bookId, not the selector, so a single
  // refetch updates every affected leaf + its rollups.
  // -------------------------------------------------------------------
  const prevInFlightRef = useRef(false);
  useEffect(() => {
    const justCompleted = prevInFlightRef.current && !unitJobInFlight;
    prevInFlightRef.current = unitJobInFlight;
    if (!justCompleted) return;
    const jobSelector = unitJob?.selector;
    if (!bookId || !jobSelector) return;
    dispatch(fetchRenderPreflight({ bookId, selector: jobSelector }));
    dispatch(fetchRenderedLines({ bookId, selector: jobSelector }));
    dispatch(fetchRenderTree({ bookId, force: true }));
  }, [dispatch, unitJobInFlight, unitJob?.selector, bookId]);

  // §6.3-followup — the inline eye-test grid runs its own (grid) render job
  // inside <EyeTestGridPanel/>, separate from the unit-render job tracked
  // above. When that grid job completes the panel fires onGridComplete; we
  // force-refetch the tree so the character's eye-test status pill repaints
  // live off the fresh RenderedGridCell counts.
  const handleGridComplete = useMemo(
    () => () => {
      if (bookId) dispatch(fetchRenderTree({ bookId, force: true }));
    },
    [dispatch, bookId]
  );



  // -------------------------------------------------------------------
  // Patch B — line play wiring. Singleton <Audio> element, lazy-resigned
  // signed URL. State:
  //   - busyLineId   — row currently fetching a URL (spinner).
  //   - playingLineId — row whose audio is loaded (playing OR paused).
  //   - isPaused     — whether that loaded audio is paused.
  // Together these drive the per-row Play / Pause / Stop controls.
  // -------------------------------------------------------------------
  const audioRef = useRef(null);
  const [busyLineId, setBusyLineId] = useState(null);
  const [playingLineId, setPlayingLineId] = useState(null);
  const [isPaused, setIsPaused] = useState(false);
  // Sequence-player state (hoisted so stopPlayback can also halt a running sequence — the two share audioRef
  // and must be mutually exclusive). The loop itself lives further down.
  const seqAbortRef = useRef(false);
  const [seqPlaying, setSeqPlaying] = useState(false);
  const [seqPaused, setSeqPaused] = useState(false);

  // Hard stop — pause, drop the singleton, clear state (single-line AND any sequence).
  const stopPlayback = useCallback(() => {
    const audio = audioRef.current;
    if (audio) {
      try {
        audio.pause();
      } catch (_) {
        /* ignore */
      }
    }
    audioRef.current = null;
    setPlayingLineId(null);
    setIsPaused(false);
    seqAbortRef.current = true;
    setSeqPlaying(false);
    setSeqPaused(false);
  }, []);

  // Pause — keep the singleton loaded so Play can resume from position.
  const pausePlayback = useCallback(() => {
    const audio = audioRef.current;
    if (!audio) return;
    try {
      audio.pause();
    } catch (_) {
      /* ignore */
    }
    setIsPaused(true);
  }, []);

  // §6.5+ Patch E — resign-on-error retry. Start `url` on a fresh singleton
  // <audio>, wiring lifecycle listeners that keep playing/paused state honest
  // (natural end clears the active row). Resolves once playback begins
  // (canplay); rejects on media error so the caller can resign + retry once.
  const startUrl = (url, lineId) =>
    new Promise((resolve, reject) => {
      const audio = new Audio();
      audioRef.current = audio;
      let settled = false;
      const onCanPlay = () => {
        if (settled) return;
        settled = true;
        resolve();
      };
      const onError = () => {
        if (settled) return;
        settled = true;
        const code = audio.error?.code;
        reject(new Error(audio.error?.message || `MediaError code=${code}`));
      };
      const onEnded = () => {
        if (audioRef.current === audio) audioRef.current = null;
        setPlayingLineId((cur) => (cur === lineId ? null : cur));
        setIsPaused(false);
      };
      audio.addEventListener("canplay", onCanPlay, { once: true });
      audio.addEventListener("error", onError, { once: true });
      audio.addEventListener("ended", onEnded, { once: true });
      audio.src = url;
      audio.load();
      audio.play().catch((e) => {
        if (settled) return;
        settled = true;
        reject(e);
      });
    });

  const handlePlayLine = async (row) => {
    if (!row || !row.uuid) return;

    // Resume when this exact line is loaded and merely paused.
    if (audioRef.current && playingLineId === row.lineId && isPaused) {
      try {
        await audioRef.current.play();
        setIsPaused(false);
        return;
      } catch (_) {
        // fall through to a fresh start
      }
    }

    // Fresh start — stop any current stream so we never have two.
    stopPlayback();

    const startWith = async (url) => {
      await startUrl(url, row.lineId);
      setPlayingLineId(row.lineId);
      setIsPaused(false);
    };

    const resignAndStart = async () => {
      setBusyLineId(row.lineId);
      try {
        const fresh = await dispatch(resignRenderedLineUrl(row.uuid)).unwrap();
        const url = fresh?.signedUrl;
        if (!url) throw new Error("resign returned no signedUrl");
        await startWith(url);
      } finally {
        setBusyLineId(null);
      }
    };

    // Happy path: cached URL still good.
    if (row.signedUrl) {
      try {
        await startWith(row.signedUrl);
        return;
      } catch (e) {
        // eslint-disable-next-line no-console
        console.warn(
          "[RenderingStudio] cached signedUrl failed; resigning",
          e?.message || e
        );
        // Fall through to resign-and-retry.
      }
    }

    try {
      await resignAndStart();
    } catch (e) {
      // eslint-disable-next-line no-console
      console.error(
        "[RenderingStudio] play failed even after resign",
        e?.message || e
      );
      stopPlayback();
    }
  };

  // Stop playback on unmount (no setState — component is going away).
  useEffect(() => {
    return () => {
      if (audioRef.current) {
        try {
          audioRef.current.pause();
        } catch (_) {
          /* ignore */
        }
        audioRef.current = null;
      }
    };
  }, []);

  // Ticks belong to the unit they were made in. Carrying them across a selection change would
  // let a click spend credits on lines the operator can no longer see.
  useEffect(() => { setSelectedLineIds(new Set()); }, [selector]);

  /**
   * The line the operator captured for a pronunciation audition.
   *
   * ⭐ ONE, NOT A COLLECTION. The workflow is "I just heard THAT" — capture, audition, decide.
   * A list of captures would be a second thing to manage and would put the operator back in the
   * position capture exists to remove: choosing among passages instead of using the one that
   * failed. Capturing again simply replaces it.
   *
   * Held here rather than in the bench because the button that fills it lives in a sibling — the
   * line table — and this page is their nearest common parent.
   */
  const [capturedLine, setCapturedLine] = useState(null);
  const handleCaptureLine = useCallback((row) => {
    setCapturedLine({ text: String(row?.text || ''), lineId: row?.lineId, speaker: row?.speaker });
  }, []);
  // ⚠️ A capture does not survive a book change. The audition renders it in the CURRENT book's
  // narrator voice, so a passage held over from another book would be heard in a voice that never
  // reads it — a take that looks like evidence and is not.
  useEffect(() => { setCapturedLine(null); }, [bookId]);

  /**
   * ─── ⭐ UTTERANCE KINDS — the table, and the act of marking a line ────────────────────────────
   *
   * `kindTable` is the SERVER's list of kinds, fetched with the group's voice assignments in one
   * call. It is held here, not in either component, because two siblings need it: the panel that
   * chooses a kind's voice and the row control that marks a line as being of that kind. Neither is
   * usable without it, and a client-side copy of a closed vocabulary is a copy that drifts.
   *
   * ⚠️ LITGROUP-KEYED, so it survives switching books within a group and is refetched when the
   * group changes. A stale table would offer voices belonging to somebody else's characters.
   */
  const [kindTable, setKindTable] = useState(null);
  // ⚠️ The LitGroup roster, which arrives WITH the table. It is not fetched from the book: the
  // book-scoped characters endpoint returns nothing in this architecture (characters hang off the
  // LitGroup), and borrowing it is what made the picker come up empty the first time.
  const [kindRoster, setKindRoster] = useState(null);
  const [kindBusy, setKindBusy] = useState(null);
  const [kindError, setKindError] = useState(null);
  // Whose voice a line borrows — the same two pieces of state the kind write keeps, for the same
  // reasons: a per-row (or "__bulk__") busy marker, and a partial-write report.
  const [voiceOfBusy, setVoiceOfBusy] = useState(null);
  const [voiceOfError, setVoiceOfError] = useState(null);
  /**
   * The labels a line's emotion may be set to, with the category each resolves to.
   *
   * ⛔ NOT the importer's 56-word list — the labels that have a REGISTRY ROW. An unmapped label falls
   * to `neutral`, which sends no audio tag, so offering one would let the operator hand-make the exact
   * defect measured on 2026-09-29 (21 unmapped labels, 26% of labelled dialogue with no direction).
   *
   * ⚠️ Global rather than per-book or per-LitGroup, because the registry is: a label means the same
   * thing in every book on the account. Fetched once per mount; it changes only in Admin.
   */
  const [emotionVocabulary, setEmotionVocabulary] = useState(null);
  const [emotionBusy, setEmotionBusy] = useState(null);
  const [emotionError, setEmotionError] = useState(null);
  // The line whose split modal is open, and the write state for both split and undo.
  const [splitRow, setSplitRow] = useState(null);
  // The ticked run a join is being composed from — null when the dialog is closed.
  const [joinRows, setJoinRows] = useState(null);
  const [splitBusy, setSplitBusy] = useState(false);
  const [splitError, setSplitError] = useState(null);
  // ⭐ Every way this book's audio differs from its text. Refetched after any write that could change it,
  // because a change list that lags is worse than none — see ChangeListPanel.
  const [changeList, setChangeList] = useState(null);
  const [changeListLoading, setChangeListLoading] = useState(false);
  const [changeListError, setChangeListError] = useState(null);

  /**
   * Is the render-unit tree collapsed to its rail? See the comment at `.rs-two-col` for why.
   *
   * ⚠️ A WORKSPACE PREFERENCE, so `localStorage` and not Redux or the graph: it belongs to this
   * browser and this person, nothing else reads it, and losing it costs one click. Wrapped because
   * storage throws in a private window and a layout choice must never break the page.
   */
  const [treeCollapsed, setTreeCollapsed] = useState(() => {
    try { return localStorage.getItem(TREE_COLLAPSED_KEY) === '1'; } catch (_) { return false; }
  });
  useEffect(() => {
    try { localStorage.setItem(TREE_COLLAPSED_KEY, treeCollapsed ? '1' : '0'); } catch (_) { /* storage unavailable */ }
  }, [treeCollapsed]);

  const reloadKindVoices = useCallback(async () => {
    if (!litGroup) { setKindTable(null); setKindRoster(null); return; }
    try {
      const r = await apiClient.get(`/litgroups/${litGroup}/kind-voices`);
      setKindTable(r?.data?.kinds || null);
      setKindRoster(r?.data?.characters || null);
      setKindError(null);
    } catch (err) {
      // Not fatal to the Studio: no table means no Kind column and no panel, which is exactly the
      // state everything was in before kinds existed. Rendering is unaffected either way.
      setKindTable(null);
      setKindRoster(null);
      setKindError(err?.response?.data?.error || err.message || "Could not load utterance kinds");
    }
  }, [litGroup]);

  useEffect(() => { reloadKindVoices(); }, [reloadKindVoices]);

  /**
   * The emotion vocabulary, once. ⚠️ Failure is not fatal: no vocabulary means the Emotion column falls
   * back to the read-only chip it has always been, which is strictly better than a picker full of
   * guesses — the same bargain the Kind column makes when its table will not load.
   */
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const r = await apiClient.get("/voice-rendering/emotion-vocabulary");
        const d = r?.data?.data || r?.data || null;
        if (!cancelled) setEmotionVocabulary(d && Array.isArray(d.labels) ? d : null);
      } catch (err) {
        if (!cancelled) {
          setEmotionVocabulary(null);
          setEmotionError(err?.response?.data?.error?.message
            || err?.response?.data?.error || err.message || "Could not load the emotion vocabulary");
        }
      }
    })();
    return () => { cancelled = true; };
  }, []);

  /**
   * Mark lines with a kind — `lineIds` null means "every ticked line".
   *
   * ⭐ REFETCHES THE ROWS AND THE PREFLIGHT, not just the local row. The point of marking a line is
   * that the RESOLVER now treats it differently, and the two things that can show that are the
   * Resolution pill and the gate. Updating the chip alone would show the decision and hide its
   * effect, which is the failure this whole feature is about.
   */
  /**
   * ⭐ OVERRULE THE CLASSIFIER'S DELIVERY on one line or on every ticked line.
   *
   * The emotion is the only input to the audio tag, so this is the smallest possible change to how a
   * line sounds — and it spends NOTHING until the line is re-rendered, which is why it is safe to use
   * liberally while reading a chapter. The operator's case for it: *"many 'neutral's could be tuned
   * even after a single listen."*
   *
   * ⚠️ Refetches the line rows afterwards rather than patching them locally, because the row must pick
   * up `renderedEmotion` alongside the new label — that comparison is what draws the amber "the audio
   * predates this" mark, and a local patch would set the label while leaving the row claiming its take
   * was current.
   */

  /**
   * ─── ⭐ SPLIT A LINE THE INGEST GLUED ─────────────────────────────────────────────────────────────
   *
   * The book is never edited: the original line keeps its words, its contentKey and its rendered take, and
   * these parts are rendered in its place. So the undo below is a real restore rather than a compensating
   * edit, and a re-ingest carries the split forward instead of reverting it.
   *
   * ⚠️ Refetches the rows AND the tree, because a split changes the NUMBER of lines. The tree counts a
   * whole book and calls its own total authoritative; leaving it stale would show 80 lines against a table
   * of 81 and make "Complete" unreachable.
   */
  /**
   * Read the change list.
   *
   * ⚠️ Called after every write that can alter it — a split, an undo, a speaker reassignment, an emotion
   * override — because a change list that lags is worse than not having one: it would assert a book is clean
   * while it is not, which is the only claim this panel exists to make.
   */
  const reloadChangeList = useCallback(async () => {
    if (!bookId) { setChangeList(null); return; }
    setChangeListLoading(true);
    setChangeListError(null);
    try {
      const r = await apiClient.get(`/voice-rendering/${bookId}/change-list`);
      setChangeList(r?.data?.data || r?.data || null);
    } catch (err) {
      // ⛔ Reported, never silently empty. "Nothing has been changed" must not be what a failed read looks
      // like — that is the difference between a record and a reassurance.
      setChangeList(null);
      setChangeListError(err?.response?.data?.error?.message || err?.response?.data?.error
        || err.message || "Could not read the change list");
    } finally {
      setChangeListLoading(false);
    }
  }, [bookId]);

  useEffect(() => { reloadChangeList(); }, [reloadChangeList]);

  const handleSplitLine = useCallback(async (payload) => {
    if (!bookId || !payload?.anchorLineId) return;
    setSplitBusy(true);
    setSplitError(null);
    try {
      await apiClient.put(`/voice-rendering/${bookId}/line-edit`, payload);
      setSplitRow(null);
      if (selector) {
        dispatch(fetchRenderedLines({ bookId, selector }));
        dispatch(fetchRenderPreflight({ bookId, selector }));
      }
      // ⚠️ force, because the tree is cached: a split changes the line COUNT, and the tree calls its own
      // total authoritative — a stale one shows 80 against a table of 81 and "Complete" never arrives.
      dispatch(fetchRenderTree({ bookId, force: true }));
      reloadChangeList();
    } catch (err) {
      setSplitError(err?.response?.data?.error?.message || err?.response?.data?.error
        || err.message || 'Could not split the line');
    } finally {
      setSplitBusy(false);
    }
  }, [bookId, selector, dispatch, reloadChangeList]);

  /**
   * ⭐ JOIN — the same endpoint and the same undo as a split, with `kind: 'join'`.
   *
   * ⚠️ The selection is CLEARED on success, deliberately: the absorbed rows no longer exist as rows,
   * so leaving them ticked would leave the toolbar offering actions against ids the table will not
   * render on the next fetch.
   */
  const handleJoinLines = useCallback(async (payload) => {
    if (!bookId || !payload?.anchorLineId) return;
    setSplitBusy("__join__");
    setSplitError(null);
    try {
      await apiClient.put(`/voice-rendering/${bookId}/line-edit`, payload);
      setJoinRows(null);
      setSelectedLineIds(new Set());
      if (selector) {
        dispatch(fetchRenderedLines({ bookId, selector }));
        dispatch(fetchRenderPreflight({ bookId, selector }));
      }
      // ⚠️ force, for the reason a split forces it: a join changes the line COUNT, and the tree calls
      // its own total authoritative — a stale one shows 81 against a table of 79.
      dispatch(fetchRenderTree({ bookId, force: true }));
      reloadChangeList();
    } catch (err) {
      setSplitError(err?.response?.data?.error?.message || err?.response?.data?.error
        || err.message || 'Could not join the lines');
    } finally {
      setSplitBusy(false);
    }
  }, [bookId, selector, dispatch, reloadChangeList]);

  /** Undo — the anchor was never mutated, so clearing the chain restores it. */
  const handleUndoSplit = useCallback(async (anchorLineId) => {
    if (!bookId || !anchorLineId) return;
    setSplitBusy(true);
    setSplitError(null);
    try {
      await apiClient.delete(`/voice-rendering/${bookId}/line-edit/${anchorLineId}`);
      if (selector) {
        dispatch(fetchRenderedLines({ bookId, selector }));
        dispatch(fetchRenderPreflight({ bookId, selector }));
      }
      // ⚠️ force, because the tree is cached: a split changes the line COUNT, and the tree calls its own
      // total authoritative — a stale one shows 80 against a table of 81 and "Complete" never arrives.
      dispatch(fetchRenderTree({ bookId, force: true }));
      reloadChangeList();
    } catch (err) {
      setSplitError(err?.response?.data?.error?.message || err?.response?.data?.error
        || err.message || 'Could not undo the split');
    } finally {
      setSplitBusy(false);
    }
  }, [bookId, selector, dispatch, reloadChangeList]);

  const handleSetLineEmotion = useCallback(async (lineIds, emotion) => {
    const ids = Array.isArray(lineIds) && lineIds.length
      ? lineIds
      : Array.from(selectedLineIds || []);
    if (!bookId || !ids.length || !emotion) return;
    setEmotionBusy(ids.length === 1 && Array.isArray(lineIds) ? ids[0] : "__bulk__");
    setEmotionError(null);
    try {
      const r = await apiClient.put(`/voice-rendering/${bookId}/line-emotion`, { lineIds: ids, emotion });
      const d = r?.data?.data || r?.data || {};
      // ⚠️ Reported, not assumed — same reason as the kind write: a lineId from another book matches
      // nothing, and "requested 23, updated 21" is the only way that becomes visible.
      if (d.updated != null && d.requested != null && d.updated !== d.requested) {
        setEmotionError(`${d.updated} of ${d.requested} lines were set — the rest were not found in this book.`);
      }
      if (selector) dispatch(fetchRenderedLines({ bookId, selector }));
      reloadChangeList();
    } catch (err) {
      setEmotionError(err?.response?.data?.error?.message || err?.response?.data?.error
        || err.message || "Could not set the emotion");
    } finally {
      setEmotionBusy(null);
    }
  }, [bookId, selector, selectedLineIds, dispatch, reloadChangeList]);

  const handleSetLineKind = useCallback(async (lineIds, kind) => {
    const ids = Array.isArray(lineIds) && lineIds.length
      ? lineIds
      : Array.from(selectedLineIds || []);
    if (!bookId || !ids.length) return;
    setKindBusy(ids.length === 1 && Array.isArray(lineIds) ? ids[0] : "__bulk__");
    setKindError(null);
    try {
      const r = await apiClient.put(`/voice-rendering/${bookId}/line-kind`, { lineIds: ids, kind });
      const d = r?.data?.data || r?.data || {};
      // ⚠️ Reported, not assumed. A lineId that belongs to another book matches nothing, and
      // "requested 23, updated 21" is the only way that ever becomes visible.
      if (d.updated != null && d.requested != null && d.updated !== d.requested) {
        setKindError(`${d.updated} of ${d.requested} lines were marked — the rest were not found in this book.`);
      }
      if (selector) {
        dispatch(fetchRenderedLines({ bookId, selector }));
        dispatch(fetchRenderPreflight({ bookId, selector }));
      }
    } catch (err) {
      setKindError(err?.response?.data?.error?.message || err?.response?.data?.error
        || err.message || "Could not set the kind");
    } finally {
      setKindBusy(null);
    }
  }, [bookId, selector, selectedLineIds, dispatch]);

  /**
   * Borrow another character's voice for these lines — `lineIds` null means "every ticked line",
   * `characterUuid` null clears it.
   *
   * ⛔ NOT A REASSIGNMENT, and it deliberately does not call one. `reassign-speaker` would move the
   * line, its count and its credit to the other character; this moves only the voice, and the speaker
   * keeps the line. The two endpoints exist side by side precisely because the app used to have to
   * fake the first to get the second.
   *
   * ⭐ REFETCHES THE ROWS AND THE PREFLIGHT, for the same reason marking a kind does: the point is
   * that the RESOLVER now answers differently, and the Resolution pill and the gate are what show it.
   */
  const handleSetLineVoiceOf = useCallback(async (lineIds, characterUuid) => {
    const ids = Array.isArray(lineIds) && lineIds.length
      ? lineIds
      : Array.from(selectedLineIds || []);
    if (!bookId || !ids.length) return;
    setVoiceOfBusy(ids.length === 1 && Array.isArray(lineIds) ? ids[0] : "__bulk__");
    setVoiceOfError(null);
    try {
      const r = await apiClient.put(`/voice-rendering/${bookId}/line-voice-of`, { lineIds: ids, characterUuid });
      const d = r?.data?.data || r?.data || {};
      // ⚠️ Reported, not assumed — same as the kind write. A lineId from another book matches nothing.
      if (d.updated != null && d.requested != null && d.updated !== d.requested) {
        setVoiceOfError(`${d.updated} of ${d.requested} lines were changed — the rest were not found in this book.`);
      }
      if (selector) {
        dispatch(fetchRenderedLines({ bookId, selector }));
        dispatch(fetchRenderPreflight({ bookId, selector }));
      }
    } catch (err) {
      setVoiceOfError(err?.response?.data?.error?.message || err?.response?.data?.error
        || err.message || "Could not set the borrowed voice");
    } finally {
      setVoiceOfBusy(null);
    }
  }, [bookId, selector, selectedLineIds, dispatch]);

  /**
   * ─── ⛔ WHICH LINES WOULD OUTRANK A KIND, keyed by lineId ──────────────────────────────────────
   *
   * Built from the rows the server already resolved, so there is ONE source of truth for "is this
   * speaker cast" and neither client re-derives it. The adjudicator panel is mounted inside this page,
   * so it reads the same map rather than asking again.
   *
   * ⚠️ Covers the SELECTED UNIT only. A proposal about a line outside the current selector is simply
   * absent, which reads as `undefined` → unknowable → no warning. That is correct: claiming a line is
   * fine because it is off-screen would be worse than saying nothing.
   */
  const voiceInfoByLineId = useMemo(() => {
    const m = {};
    (lineSet || []).forEach((r) => {
      if (r && r.lineId) m[r.lineId] = { speaker: r.speaker, speakerHasOwnVoice: r.speakerHasOwnVoice };
    });
    return m;
  }, [lineSet]);

  /**
   * Borrow the kind's own voice for one line — the second half of a decision the kind alone cannot
   * carry out when the speaker is cast.
   *
   * ⭐ A SEPARATE, EXPLICIT ACT, never folded into applying the kind. Setting `spokenInVoiceOf` really
   * does change the audio, and after seven confirmation proposals were applied as reassignments this
   * session, a button here does exactly and only what it says.
   */
  const borrowKindVoice = useCallback(async (lineId, kind) => {
    const entry = (kindTable || []).find((k) => k.kind === kind);
    if (!entry || !entry.characterUuid) {
      setVoiceOfError(`"${kind}" has no voice assigned, so there is nothing to borrow. Set one in "Who reads what kind".`);
      return;
    }
    await handleSetLineVoiceOf([lineId], entry.characterUuid);
  }, [kindTable, handleSetLineVoiceOf]);

  const liveLineActions = useMemo(
    () => ({
      selectedLineIds,
      onToggleLine: toggleLineSelected,
      onSetLinesSelected: setLinesSelected,
      onPlay: handlePlayLine,
      onPause: pausePlayback,
      onStop: stopPlayback,
      onCapture: handleCaptureLine,
      // The kinds vocabulary and the act of marking a line with one. Absent → no Kind column.
      kindTable,
      onSetLineKind: handleSetLineKind,
      kindBusy,
      // ⭐ HOW a line is delivered. Absent vocabulary → the Emotion column stays the read-only chip it
      // has always been, so this degrades to the prior behaviour rather than to a broken control.
      emotionVocabulary,
      onSetLineEmotion: handleSetLineEmotion,
      emotionBusy,
      // ⭐ Split a glued line, and undo one. See LINE_EDIT_CHAIN_SPEC.md.
      onOpenSplit: setSplitRow,
      // ⭐ Put a broken utterance back together — the counterpart to the split above.
      onOpenJoin: setJoinRows,
      onUndoSplit: handleUndoSplit,
      splitBusy,
      // ⭐ The roster to borrow a voice FROM, and the act of borrowing one. Absent → no "Voice of"
      // column. The same `kindRoster` the kind-voices panel picks from — one read, two questions.
      voiceOfRoster: kindRoster,
      onSetLineVoiceOf: handleSetLineVoiceOf,
      voiceOfBusy,
      busyLineId,
      playingLineId,
      isPaused,
      // ⭐ Needed by the per-line takes list, which calls the render API directly: the row knows its
      // lineId but not which book it belongs to.
      bookId,
      // After a restore the row's audio, its takeCount and the STITCH GATE all change — the chapter's
      // stitch now predates the take in use, which StitchReadiness reports as stitch-stale. Refetch
      // both rather than leave the table describing the state before the click.
      onTakeRestored: () => {
        if (!bookId || !selector) return;
        dispatch(fetchRenderedLines({ bookId, selector }));
        // ⚠️ The STITCH scope, not the render selector — the readiness verdict is only trustworthy for
        // the scope it was measured on, which is why the gate re-runs per scope elsewhere too.
        if (stitchScope) dispatch(fetchStitchReadiness({ bookId, selector: stitchScope }));
      },
    }),
    // handlePlayLine reads playingLineId/isPaused/busyLineId (all in deps) and
    // otherwise closes over stable dispatch + setters.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [busyLineId, playingLineId, isPaused, pausePlayback, stopPlayback, selectedLineIds, handleCaptureLine,
      kindTable, handleSetLineKind, kindBusy, kindRoster, handleSetLineVoiceOf, voiceOfBusy,
      bookId, selector, stitchScope]
  );

  // -------------------------------------------------------------------
  // Sequence player — play a unit's rendered lines in PRODUCTION ORDER,
  // auto-advancing with the same inter-line pauses the stitcher uses
  // (1000ms after an announcement, 500ms on a speaker switch, else 250ms).
  // Reuses the singleton audioRef + resign-on-error. Pause = pause the
  // current clip (the loop awaits its 'ended', so it simply holds).
  // (seqAbortRef / seqPlaying / seqPaused are declared up top so stopPlayback can halt the sequence.)
  // -------------------------------------------------------------------
  // ⚠️ A HAND-COPY of the server's ladder (ChapterAssembler.computePauseMs), and it has
  // already drifted once: the scene-break beat shipped server-side and never arrived here,
  // so every Play-in-Sequence audition has been hearing scene breaks at 250/500 ms while the
  // stitched file had 900. Sidenotes are synced below; SCENE BREAKS STILL CANNOT BE — a
  // `sceneBreak` is project:null, has no audio, and `playable` filters to status "rendered",
  // so the marker is gone before this runs. The server preserves the information by flagging
  // the row that follows (selectStitchRows → precededBySceneBreak); the client's rows carry
  // no such flag.
  //
  // So PLAY-IN-SEQUENCE IS AN APPROXIMATION OF THE STITCH, not a preview of it. The durable
  // fix is for the rows endpoint to send `pauseAfterMs` per row so one ladder serves both.
  const seqPauseMs = (row, next) => {
    if (!next) return 0;
    if (row.type === "announcement" || row.type === "chapter") return 1000;
    // A sidenote is an excursion out of the narrative and back, so the beat falls on both
    // sides. Mirrors PAUSE_NOTE_MS; the server value is env-tunable and this one is not,
    // which is another reason the two should eventually come from one place.
    if (row.type === "note" || next.type === "note") return 900;
    if ((row.speaker || "") !== (next.speaker || "")) return 500;
    return 250;
  };

  // Play ONE row to its natural end (resolve on 'ended'); resign + retry once on media error.
  const playRowToEnd = (row) => {
    const run = (url) =>
      new Promise((resolve, reject) => {
        const audio = new Audio();
        audioRef.current = audio;
        audio.addEventListener("ended", () => resolve(), { once: true });
        audio.addEventListener("error", () => reject(new Error(audio.error?.message || "media error")), { once: true });
        audio.src = url;
        audio.load();
        audio.play().catch(reject);
      });
    return (async () => {
      // ⚠️ ALWAYS RESIGN — do not reuse row.signedUrl, however fresh it looks.
      //
      // Every take of a line OVERWRITES ONE S3 KEY
      // (.../rendered-lines/frontmatter:0:opening-credits.mp3), so the URL cannot distinguish
      // this render from the last one. Replaying a held URL let the browser's HTTP cache serve a
      // previous take: the operator re-rendered a line, heard it correctly on its own, and heard
      // the OLD audio — words missing — in Play-in-sequence, while the bytes in S3 were provably
      // correct. It cost a long evening to find, because every layer measured clean.
      //
      // A fresh presign carries a new X-Amz-Date and signature, so it is a genuinely different
      // URL and cannot hit a stale cache entry.
      //
      // ⛔ Do NOT "optimise" this by appending a cache-busting query param instead. SigV4 signs
      // the canonical query string, so an extra param yields 403 SignatureDoesNotMatch — which
      // was tried, shipped, and broke playback outright: every line 403'd, the error handler
      // fired instantly, and the sequencer raced through flashing each play button.
      //
      // The cost is one presign per line. That is a signing operation, not an S3 round trip.
      const fresh = await dispatch(resignRenderedLineUrl(row.uuid)).unwrap();
      const url = fresh?.signedUrl || row.signedUrl;
      if (!url) throw new Error("no playable URL for this line");
      await run(url);
    })();
  };

  const stopSequence = useCallback(() => {
    seqAbortRef.current = true;
    const a = audioRef.current;
    if (a) { try { a.pause(); } catch (_) { /* ignore */ } }
    audioRef.current = null;
    setSeqPlaying(false);
    setSeqPaused(false);
    setPlayingLineId(null);
  }, []);
  const pauseSequence = useCallback(() => {
    const a = audioRef.current;
    if (a) { try { a.pause(); } catch (_) { /* ignore */ } }
    setSeqPaused(true);
  }, []);
  const resumeSequence = useCallback(() => {
    const a = audioRef.current;
    if (a) a.play().catch(() => { });
    setSeqPaused(false);
  }, []);

  const playSequence = useCallback(
    async (rows) => {
      const playable = (rows || []).filter(
        (r) => r && r.uuid && r.status === "rendered" && String(r.text || "").trim()
      );
      if (!playable.length) return;
      stopPlayback();               // never overlap with the single-line player
      seqAbortRef.current = false;
      setSeqPlaying(true);
      setSeqPaused(false);
      for (let i = 0; i < playable.length; i++) {
        if (seqAbortRef.current) break;
        const row = playable[i];
        setPlayingLineId(row.lineId);
        try { await playRowToEnd(row); } catch (_) { /* skip a failed clip, keep going */ }
        if (seqAbortRef.current) break;
        const ms = seqPauseMs(row, playable[i + 1]);
        if (ms) await new Promise((res) => setTimeout(res, ms));
      }
      if (!seqAbortRef.current) {
        setSeqPlaying(false);
        setSeqPaused(false);
        setPlayingLineId(null);
      }
    },
    // playRowToEnd/seqPauseMs close over stable dispatch + setters + audioRef.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [stopPlayback]
  );

  const sequenceActions = useMemo(
    () => ({
      onPlaySequence: playSequence,   // (rows) => …
      onPause: pauseSequence,
      onResume: resumeSequence,
      onStop: stopSequence,
      playing: seqPlaying,
      paused: seqPaused,
    }),
    [playSequence, pauseSequence, resumeSequence, stopSequence, seqPlaying, seqPaused]
  );

  // Per-line render OUTCOMES from the last job for THIS unit. The RenderJob
  // snapshot carries each line's terminal status ('success'|'cached'|'skipped'
  // |'failed') plus reason/error — the "6/6 done, but 1 produced no audio"
  // detail the line-set's rendered/not-rendered view can't express. Keyed by
  // lineId; only surfaced when the job's selector matches the selected unit.
  const lineOutcomes = useMemo(() => {
    if (
      !unitJob ||
      unitJob.selector !== selector ||
      !Array.isArray(unitJob.rendered)
    ) {
      return null;
    }
    const map = {};
    for (const r of unitJob.rendered) {
      if (r && r.lineId) {
        map[r.lineId] = {
          status: r.status,
          reason: r.reason || null,
          error: r.error || null,
        };
      }
    }
    return map;
  }, [unitJob, selector]);

  // The `liveActions` prop for RenderUnitDetail. When a node is selected
  // and a book is picked, the detail pane's primary "Render" button is
  // live; the rest of its action bar stays inert.
  const liveActions = useMemo(() => {
    if (!selectedNode || !bookId) return null;
    const isRed = preflight?.status === "red";
    const isExec = selectedNode.executable !== false; // tolerate missing flag
    const myJobActive =
      !!unitJob && unitJob.selector === selector && unitJobInFlight;
    const busy = myJobActive || unitJobLoading;
    // Which line is in flight right now? The RenderJob loop marks exactly one
    // row `rendering` at a time (sequential per line), so surface it under the
    // Render button as a "currently rendering" hint. Falls back to the done/
    // total counters between lines (e.g. a burst of cache hits).
    const inFlightRow =
      myJobActive && Array.isArray(unitJob?.rendered)
        ? unitJob.rendered.find((r) => r.status === "rendering") || null
        : null;
    return {
      onRender: handleStartRender,
      // Live for every executable unit, front and rear matter included — the only unit kinds
      // whose takes could not be replaced before this existed.
      onReRender: handleReRender,
      onReRenderLines: handleReRenderLines,
      // Downloading reads what is already rendered, so unlike the render actions it does NOT
      // depend on the unit being executable or on a job being idle.
      onDownloadLines: handleDownloadLines,
      downloadBusy: downloadingLines,
      onStitchLines: handleStitchLines,
      stitchLinesBusy: stitchingLines,
      reRenderDisabled: !isExec || busy || preflightLoading,
      reRenderCount: selectedNode?.counts?.lines ?? 0,
      disabled: !isExec || busy || preflightLoading,
      busy: !!myJobActive,
      /**
       * ⭐ STOP A RENDER IN FLIGHT. Until now the stitch and the mastering stages could both be
       * stopped and the RENDER could not — the one that spends ElevenLabs characters and runs
       * longest. The operator asked for it after watching a render and spotting a line that
       * needed joining.
       *
       * ⚠️ Cooperative, like the stitch: the line in flight finishes and is persisted, so every
       * line before the stop is finished work and nothing needs cleaning up.
       */
      onStop: myJobActive && unitJob?.jobId
        ? () => dispatch(stopUnitRender(unitJob.jobId))
        : null,
      stopping: !!unitJob?.stopRequested,
      label: myJobActive ? "Rendering…" : isRed ? "Render anyway" : "Render",
      progress: myJobActive
        ? {
          current: inFlightRow,
          done: unitJob?.totals?.done ?? 0,
          total: unitJob?.totals?.total ?? selectedNode?.counts?.lines ?? 0,
        }
        : null,
      /**
       * ─── ⭐ THE FINAL COUNT, WHICH NOTHING USED TO SHOW ──────────────────────────────────────────
       *
       * Operator, 2026-09-27: *"the render status line show 1 less completed than the total value - when
       * render was complete… which could be because the status line is rendered after the 'completed'
       * path."* Right in substance. The progress hint only renders while the job is BUSY, and `progress`
       * above goes null the instant it stops — so the sequence was:
       *
       *     poll N     done=2074, busy=true   → "2074 of 2075 complete"
       *     job ends   done=2075, busy=false  → the hint unmounts
       *
       * The last frame ever drawn was `total - 1`. ⛔ The server's accounting is fine — all four exit
       * paths in RenderJob increment `done` (skipped, cached, success, error) — so the number was right
       * and simply never displayed.
       *
       * ⚠️ Which cost more than tidiness: a render ending on an ambiguous `2074 of 2075` left the
       * operator unsure whether the book had finished, and the question had to be answered by querying
       * the graph. A terminal line answers it on screen.
       */
      finished: !myJobActive && unitJob?.totals?.total
        ? {
          done: unitJob.totals.done ?? 0,
          total: unitJob.totals.total,
          skipped: unitJob.totals.skipped ?? 0,
          errors: unitJob.totals.errors ?? 0,
          cached: unitJob.totals.cached ?? 0,
          status: unitJob.status || null,
        }
        : null,
      tooltip: !isExec
        ? "This unit kind isn't executable yet."
        : isRed
          ? "Preflight is RED. Clicking will prompt for force-render."
          : "Start a render for this unit.",
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    selectedNode,
    bookId,
    dispatch,
    selector,
    preflight?.status,
    preflightLoading,
    unitJob,
    unitJobInFlight,
    unitJobLoading,
    // ⚠️ REQUIRED. onReRenderLines closes over this Set; without it the memo keeps a handler
    // from a render where the selection was empty, so the click hits the size===0 guard and
    // returns silently — the button reads "(1)" while the handler still sees zero. The
    // exhaustive-deps disable above is what let that ship.
    selectedLineIds,
    // Same reasoning: the download handler closes over the same Set, and its busy flag has to
    // reach the button or it can be clicked twice while the first batch is still saving.
    downloadingLines,
    // …and the stitch handler for the same reasons, plus `lineSet`-independent `selector`, already
    // listed above: stale, it would stitch an empty or out-of-date selection without a word.
    stitchingLines,
  ]);

  // -------------------------------------------------------------------
  // Cleanup — on bookId change & on unmount, drop per-selector caches and
  // any in-flight job. The render-tree cache stays (TTL handles staleness).
  // -------------------------------------------------------------------
  useEffect(() => {
    return () => {
      dispatch(clearUnitJob());
      dispatch(clearStitchJob());
      dispatch(clearPreflight({}));
      dispatch(clearRenderedLines({}));
    };
  }, [dispatch, bookId]);

  // -------------------------------------------------------------------
  // Activity log (Chunk 5) — §6.6 A9 (follow-up): a server-side activity-log
  // endpoint will feed this. Until then we render an empty log rather than
  // fabricate entries from in-memory state.
  // -------------------------------------------------------------------
  const activity = useMemo(() => [], []);

  // Standalone legacy R1 grid lives behind a modal, opened from the header.
  const [legacyModalOpen, setLegacyModalOpen] = useState(false);

  // Confirm dialog body — Confirm accepts a string only, so we serialize
  // the blocker list into a single readable line.
  const confirmContent = useMemo(() => {
    const blockers = (unitJobBlocked?.preflight?.blockers || [])
      .map((b) => (typeof b === "string" ? b : b?.message || b?.code))
      .filter(Boolean);
    const tail = blockers.length
      ? ` Blockers: ${blockers.join("; ")}.`
      : "";
    return (
      "Preflight is RED. Some lines may fall back to category presets or " +
      "fail outright. You can still proceed with force=true." +
      tail
    );
  }, [unitJobBlocked]);

  return (
    <Container fluid className="rendering-studio rs-mockup">
      {/* Mounted by the PAGE, not by Layout — the Rendering Studio is normally reached as a
          dashboard sub-page, where the URL stays /dashBoard and route-matching misses it. */}
      <ProcessGuide guideKey="rendering-studio" />
      <Container style={{ marginTop: "1.5rem", marginBottom: "2rem" }}>
        <div className="rs-page-headerbar">
          <Header as="h1" dividing className="rs-page-title">
            <Icon name="microphone" />
            <Header.Content>
              Rendering Studio
              <Header.Subheader>
                Render-unit tree, preflight gate, and per-character eye-test —
                qualified by LitGroup + Book.
              </Header.Subheader>
            </Header.Content>
          </Header>
          <Button
            primary
            icon
            labelPosition="left"
            className="rs-open-legacy-btn"
            onClick={() => setLegacyModalOpen(true)}
          >
            <Icon name="external alternate" />
            Open eye-test studio
          </Button>
        </div>

        <Message icon info size="small" className="rs-mockup-notice">
          <Icon name="microphone" />
          <Message.Content>
            <Message.Header>Live rendering</Message.Header>
            Pick a LitGroup + Book, choose a render unit in the tree, and
            render it. Per-character eye-test grids render inline in the detail
            pane; preflight gates each unit before it burns credits.
          </Message.Content>
        </Message>

        {/* LitGroup + Book picker strip — both live. */}
        <Segment className="rs-pickers">
          <div className="rs-pickers-row">
            <div className="rs-picker-group">
              <Icon name="folder open outline" className="rs-picker-icon" />
              <label className="rs-picker-label">LitGroup</label>
              <Dropdown
                selection
                fluid
                options={litGroupOptions}
                value={litGroup || ""}
                loading={litGroupsStatus === "loading" && litGroups.length === 0}
                placeholder="Select a Literary Group…"
                onChange={(_, { value }) => {
                  setLitGroup(value || null);

                  setBookId(null);
                  setSelectedId(null);
                  dispatch(clearUnitJob());
                }}
                className="rs-picker-dropdown"
              />
            </div>
            <div className="rs-picker-group">
              <Icon name="book" className="rs-picker-icon" />
              <label className="rs-picker-label">Book</label>
              <Dropdown
                selection
                fluid
                options={bookOptions}
                value={bookId || ""}
                loading={booksStatus === "loading"}
                placeholder={
                  litGroup ? "Select a book…" : "Pick a Literary Group first…"
                }
                disabled={!litGroup}
                onChange={(_, { value }) => {
                  setBookId(value || null);
                  setSelectedId(null);
                  dispatch(clearUnitJob());
                }}
                className="rs-picker-dropdown"
              />
            </div>
          </div>
          <div className="rs-pickers-hint">
            <Icon name="info circle" /> Both pickers drive the render-unit
            tree below — independent of the Voice Cloning Pipeline. A book
            without any cloned characters is still a valid selection
            (preflight will report the blocked characters per unit).
          </div>
          {/* ⛔ NO BOOK, NO ACTIONS. Everything below — stitch scope, Preflight, Stitch, Export,
              the Audio Lab, the readiness verdict — acts on `bookId`, and every one of them either
              spends money or reports a status. Rendering them before a book is chosen showed the
              operator a fully-armed control strip describing a book he had not selected: on
              2026-08-24 the page opened on the newest LitGroup, the Book box read "Select a book…",
              and the rest of the page was filled in from a book nobody picked.
              ⚠️ The pickers are now genuinely FIRST in the markup, not merely first on screen
              because this happened to be hidden. Both matter and neither replaces the other: the
              order is what a reader meets, the guard is what stops an unchosen book being armed. */}
          {bookId && (
            <>
              {/* Separates WHAT you have chosen from WHAT YOU CAN DO TO IT. Inside the guard on
                  purpose: with no book there is nothing below to divide, and a rule across an
                  otherwise-empty page would imply something was missing. */}
              <Divider />
              {/* R3c — stitch actions, scoped to a selectable chunk. Below the pickers. */}
          <div
            className="rs-stitch-bar"
            style={{ display: "flex", gap: 8, alignItems: "center", flexWrap: "wrap", marginBottom: 12 }}
          >
            <span style={{ fontWeight: 600, fontSize: "0.9em" }}>
              <Icon name="cut" /> Stitch scope
            </span>
            <Dropdown
              selection
              compact
              scrolling
              options={stitchScopeOptions}
              value={stitchScope}
              disabled={!bookId || stitchJobInFlight}
              onChange={(_, { value }) => {
                setStitchScope(value);
                setStitchMsg(null);
              }}
              style={{ minWidth: 220 }}
            />
            <Button
              size="small"
              icon
              labelPosition="left"
              disabled={!bookId || stitchBusy || stitchJobInFlight}
              loading={stitchBusy}
              onClick={runPreflightStitch}
            >
              <Icon name="clipboard check" />
              Preflight
            </Button>
            <Button
              size="small"
              color="teal"
              icon
              labelPosition="left"
              disabled={!scopeReady || stitchBusy || stitchJobLoading || stitchJobInFlight}
              loading={stitchJobLoading || stitchJobInFlight}
              title={
                !scopeReady
                  // ⚠️ Names the RED condition, since amber no longer disables this.
                  ? "Enabled once every line in the selected scope has rendered audio (Preflight must not be red)"
                  : scopeWarnings.length
                    ? `Assemble the rendered lines of this scope into chapter audio.\n\n⚠️ ${scopeWarnings.join("\n⚠️ ")}`
                    : "Assemble the rendered lines of this scope into chapter audio"
              }
              onClick={() => runStitch()}
            >
              <Icon name="object group" />
              {stitchScope === "book.body" ? "Stitch Book" : "Stitch Chunk"}
            </Button>
            {/**
              * ⭐ FORCE RE-STITCH. The ordinary Stitch builds only what is not already current, which
              * is right for a resume after a crash and wrong when the STITCH CODE changed — pause
              * lengths and gap generation alter every chapter's audio while leaving every chapter's
              * timestamp newer than its lines. Secondary and confirmed, because it re-encodes the
              * whole unit. Mirrors the Audio Lab's "Force all" for mastering.
              */}
            <Button
              size="small"
              basic
              icon
              labelPosition="left"
              disabled={!scopeReady || stitchBusy || stitchJobLoading || stitchJobInFlight}
              title={
                "Rebuild every chapter even if it looks current. Needed when the stitch itself "
                + "changed — pause lengths, gap generation — because a chapter is judged current "
                + "only against its lines' timestamps, not against the code that built it."
              }
              onClick={() => setConfirmRestitch(true)}
            >
              <Icon name="redo" />
              Force re-stitch
            </Button>
            <Button
              size="small"
              icon
              labelPosition="left"
              disabled={!bookId || exporting || stitchJobInFlight}
              loading={exporting}
              title="Package this scope's stitched chapters into one zip in S3 and return a shareable link"
              onClick={runExport}
            >
              <Icon name="download" />
              Export zip
            </Button>
            {stitchReadiness && (
              <Label
                size="large"
                color={
                  stitchReadiness.status === "green"
                    ? "green"
                    : stitchReadiness.status === "amber"
                      ? "yellow"
                      : "red"
                }
              >
                <Icon name={stitchReadiness.status === "green" ? "check circle" : "exclamation circle"} />
                {stitchReadiness.renderedCount}/{stitchReadiness.lineCount} rendered
                {stitchReadiness.missingCount > 0
                  ? ` · ${stitchReadiness.missingCount} missing`
                  : " · ready to stitch"}
                {stitchReadiness.selector ? ` · ${stitchReadiness.selector}` : ""}
              </Label>
            )}
          </div>
          {stitchReadiness && stitchReadiness.selector !== stitchScope && (
            <Message size="mini" warning style={{ marginTop: 0 }}>
              <Icon name="info circle" /> The verdict above is for{" "}
              <code>{stitchReadiness.selector}</code>. Re-run Preflight to check{" "}
              <code>{stitchScope}</code> before stitching it.
            </Message>
          )}
          {/* ⚠️ A download that saved nothing must SAY so. Every file lands in the browser's own
              download tray, outside this page, so without a receipt here "nothing happened" and
              "twelve files saved" look identical on screen. */}
          {downloadNote && (
            <Message
              size="small"
              negative={downloadNote.negative}
              positive={!downloadNote.negative && downloadNote.saved > 0}
              onDismiss={() => setDownloadNote(null)}
            >
              <Icon name={downloadNote.negative ? "warning" : "download"} />
              {downloadNote.saved > 0
                ? `Saved ${downloadNote.saved} MP3 file${downloadNote.saved === 1 ? "" : "s"}.`
                : "No files were saved."}
              {downloadNote.skipped > 0
                && ` ${downloadNote.skipped} selected line${downloadNote.skipped === 1 ? " has" : "s have"} not been rendered yet.`}
              {downloadNote.failed > 0
                && ` ${downloadNote.failed} could not be fetched.`}
            </Message>
          )}
          {stitchLinesNote && (
            <Message
              size="small"
              negative={!!stitchLinesNote.error}
              positive={!stitchLinesNote.error && !(stitchLinesNote.notRendered || stitchLinesNote.notInUnit)}
              warning={!stitchLinesNote.error && !!(stitchLinesNote.notRendered || stitchLinesNote.notInUnit)}
              onDismiss={() => setStitchLinesNote(null)}
            >
              <Icon name={stitchLinesNote.error ? "warning" : "file audio"} />
              {stitchLinesNote.error
                ? `Stitch failed: ${stitchLinesNote.error}`
                : `Stitched ${stitchLinesNote.lines} line${stitchLinesNote.lines === 1 ? "" : "s"} into ${stitchLinesNote.name} (${stitchLinesNote.seconds}s).`}
              {!stitchLinesNote.error && stitchLinesNote.notRendered > 0
                && ` ${stitchLinesNote.notRendered} selected line${stitchLinesNote.notRendered === 1 ? " was" : "s were"} left out — not rendered yet.`}
              {!stitchLinesNote.error && stitchLinesNote.notInUnit > 0
                && ` ${stitchLinesNote.notInUnit} ticked line${stitchLinesNote.notInUnit === 1 ? " belongs" : "s belong"} to another unit and ${stitchLinesNote.notInUnit === 1 ? "was" : "were"} left out.`}
            </Message>
          )}
          {stitchError && (
            <Message negative size="small">
              <Icon name="warning" /> Preflight failed: {stitchError}
            </Message>
          )}
          {stitchJobError && (
            <Message negative size="small">
              <Icon name="warning" /> Stitch failed: {stitchJobError}
            </Message>
          )}
          {stitchMsg && (
            <Message info size="small" onDismiss={() => setStitchMsg(null)}>
              <Icon name="info circle" /> {stitchMsg}
            </Message>
          )}
          {exportError && (
            <Message negative size="small" onDismiss={() => setExportError(null)}>
              <Message.Header>Export failed</Message.Header>
              <p>{exportError}</p>
            </Message>
          )}
          {exportResult && (
            <Message positive size="small" onDismiss={() => setExportResult(null)}>
              <Message.Header>
                <Icon name="file archive outline" /> {exportResult.chapters} chapter
                {exportResult.chapters === 1 ? "" : "s"} packaged
                {exportResult.totalBytes
                  ? ` — ${(exportResult.totalBytes / 1048576).toFixed(0)} MB`
                  : ""}
              </Message.Header>
              {/* A short zip must never look like a complete one. */}
              {exportResult.failures?.length > 0 && (
                <p style={{ color: "#912d2b", fontWeight: 600 }}>
                  <Icon name="warning sign" />
                  {exportResult.failures.length} chapter
                  {exportResult.failures.length === 1 ? "" : "s"} could not be read and{" "}
                  {exportResult.failures.length === 1 ? "is" : "are"} MISSING from this zip
                  (ch{" "}
                  {exportResult.failures.map((f) => f.chapterNumber).join(", ")}).
                </p>
              )}
              <div style={{ display: "flex", gap: 8, alignItems: "center", marginTop: 8, flexWrap: "wrap" }}>
                <Button
                  size="mini"
                  icon
                  labelPosition="left"
                  color={copied ? "green" : undefined}
                  onClick={() => {
                    navigator.clipboard?.writeText(exportResult.signedUrl);
                    setCopied(true);
                  }}
                >
                  <Icon name={copied ? "check" : "copy"} />
                  {copied ? "Link copied" : "Copy share link"}
                </Button>
                <Button
                  size="mini"
                  icon
                  labelPosition="left"
                  as="a"
                  href={exportResult.signedUrl}
                  target="_blank"
                  rel="noreferrer"
                >
                  <Icon name="download" />
                  Download
                </Button>
              </div>
              {/* State the security property plainly — a signed URL is private-by-obscurity
                  with an expiry, NOT access control, and the person sharing it needs to know. */}
              <p style={{ marginTop: 10, fontSize: "0.85em", opacity: 0.85 }}>
                <Icon name="lock open" /> Anyone with this link can download it until{" "}
                <strong>
                  {exportResult.expiresAt
                    ? new Date(exportResult.expiresAt).toLocaleString()
                    : "it expires"}
                </strong>
                . It isn’t password-protected — share it only with people you intend to
                have it. Re-export any time for a fresh link.
              </p>
            </Message>
          )}
          {/*
            The Audio Lab reads DURABLE :RenderedChapter rows, not the in-memory stitch job —
            so it still shows a completed stitch after a server restart, when `stitchJob`
            below is gone and the panel would otherwise look like nothing had ever been
            stitched. That absence is what made re-stitching look mandatory.
          */}
          {/**
            * ⭐ `refreshSignal` — the Audio Lab reads `:RenderedChapter` and cannot see the stitch
            * job, which lives here. Without this a finished 44-chapter stitch left the panel showing
            * "No stitched data…", which on 2026-10-01 read as a stitch still running and nearly
            * caused a completed job to be re-run. Any CHANGING value works; the finished job's id is
            * stable while the job stands and differs for the next one.
            *
            * ⚠️ Deliberately only on a TERMINAL stitch: signalling mid-run would re-fetch the
            * durable report every poll, for chapters that are still being written.
            */}
          <AudioLabPanel
            bookId={bookId}
            selector={stitchScope}
            unitLabel={stitchScopeLabel}
            refreshSignal={
              stitchJob && stitchJob.status !== "running"
                ? `${stitchJob.jobId}:${stitchJob.status}`
                : null
            }
          />

          {/*
            DELIVERY sits AFTER the Audio Lab deliberately. The Lab is where a chapter is judged one at
            a time; this is the question asked once, at the end — is the book finished, and is the file
            I am about to send actually the book? ⛔ It exists because the finished export was the only
            artifact nobody checked, and its share link lived in React state: 1,217 MB sat intact in
            storage and unreachable from the app after a single reload.
          */}
          <DeliveryPanel
            bookId={bookId}
            selector={stitchScope}
            unitLabel={stitchScopeLabel}
          />

          {/* The LitGroup's respellings, and the audition that decides them. Here rather than in
              the Ingest Lab because a pronunciation fault is found BY EAR, after a render — so the
              map has to be addable at the moment you discover you need one. */}
          <PronunciationBench
            litGroupUuid={litGroup}
            bookId={bookId}
            bookTitle={(bookOptions.find((b) => b.value === bookId) || {}).text}
            captured={capturedLine}
            onClearCapture={() => setCapturedLine(null)}
          />

          {/* Beside the pronunciation register because the two are the same kind of thing one level
              apart: a respelling changes a WORD for the whole Literary Group, a correction changes a
              SPAN in named occurrences of THIS BOOK's lines. Both are found the same way — by ear or
              by eye, after a render — so both have to be reachable at the moment of discovery. */}
          {/* ⭐ The split modal — beside the editorial bench because they answer adjacent questions: the
              bench changes what a line SAYS, this changes how many lines there are. Both are discovered the
              same way, by reading a row after a render. See LINE_EDIT_CHAIN_SPEC.md. */}
          <LineSplitModal
            open={!!splitRow}
            row={splitRow}
            busy={splitBusy}
            error={splitError}
            onClose={() => { setSplitRow(null); setSplitError(null); }}
            onSplit={handleSplitLine}
          />
          {/* ⭐ And its counterpart. A split answers "this row is two lines"; a join answers "these
              rows are one" — the shape the import leaves behind when a font change ends a paragraph
              mid-sentence. Same endpoint, same undo. */}
          <LineJoinModal
            open={!!joinRows}
            rows={joinRows}
            roster={kindRoster}
            // ⭐ The fragments were never classified, so the joined line is the first point an emotion
            // can be chosen at all. Same vocabulary the row's Emotion column uses.
            emotionVocabulary={emotionVocabulary}
            busy={splitBusy === "__join__"}
            error={splitError}
            onClose={() => { setJoinRows(null); setSplitError(null); }}
            onJoin={handleJoinLines}
          />
          {/* ⚠️ Shown outside the modal too, because an UNDO failure has no modal to land in. */}
          {splitError && !splitRow && (
            <Message size="tiny" warning>
              <Icon name="warning sign" /> {String(splitError)}
            </Message>
          )}
          <EditorialBench
            litGroupUuid={litGroup}
            bookId={bookId}
            bookTitle={(bookOptions.find((b) => b.value === bookId) || {}).text}
            captured={capturedLine}
            onClearCapture={() => setCapturedLine(null)}
          />

          {/* ⭐ Beside the pronunciation, kind and editorial panels because it feeds them: its proposals
              are applied with the reassign and set-kind the Studio already owns, and the kind it
              suggests only changes audio once that kind has a voice in the panel above. */}
          <AdjudicatorPanel
            litGroupUuid={litGroup}
            bookId={bookId}
            bookTitle={(bookOptions.find((b) => b.value === bookId) || {}).text}
            // ⛔ So the panel can say that a KIND proposal will be outranked BEFORE it is applied. One
            // source of truth: these come from the rows the server already resolved, not a second
            // derivation. Absent for a line outside the selected unit, which reads as unknowable.
            lineVoiceInfo={voiceInfoByLineId}
            // The second half of a kind decision the kind alone cannot carry out, offered explicitly.
            onBorrowKindVoice={borrowKindVoice}
            onApplied={() => {
              // A reassign or a kind changes what the rows resolve to and what preflight says, so both
              // are refetched rather than left describing the state before the click.
              if (bookId && selector) {
                dispatch(fetchRenderedLines({ bookId, selector }));
                dispatch(fetchRenderPreflight({ bookId, selector }));
              }
            }}
          />

          {/* Beside the pronunciation register because they are the same kind of thing: a decision
              that belongs to the whole Literary Group, discovered while working one book of it. */}
          {kindError && (
            <Message size="tiny" warning>
              <Icon name="warning sign" /> {String(kindError)}
            </Message>
          )}
          {voiceOfError && (
            <Message size="tiny" warning>
              <Icon name="warning sign" /> {String(voiceOfError)}
            </Message>
          )}
          {/* ⛔ Shown, because the server REFUSES a label it cannot map to a category — and a refusal
              that is swallowed looks exactly like a label that was accepted. That is the failure this
              whole column exists to prevent. */}
          {emotionError && (
            <Message size="tiny" warning>
              <Icon name="warning sign" /> {String(emotionError)}
            </Message>
          )}
          <KindVoicePanel
            litGroupUuid={litGroup}
            kinds={kindTable}
            characters={kindRoster}
            onChanged={async () => {
              await reloadKindVoices();
              // The voice a kind lends is applied at RESOLVE, so the rows and the gate both change
              // the moment it is chosen — showing the new choice without them would be half a truth.
              if (bookId && selector) {
                dispatch(fetchRenderedLines({ bookId, selector }));
                dispatch(fetchRenderPreflight({ bookId, selector }));
              }
            }}
          />
          {/*
            ⚠️ TWO DIFFERENT 409s ARRIVE HERE, and this used to report both as the first one. The
            header said "scope not fully rendered" unconditionally — so the new in-flight refusal
            (added 2026-09-27, after two concurrent whole-book stitches crashed the server) would have
            told the operator their lines were unrendered when 2,075 of 2,075 were done and the real
            reason was a job already running. Branch on the server's code and use its own words.
          */}
          {stitchBlocked && (
            <Message warning size="small">
              <Message.Header>
                {stitchBlocked.code === "STITCH_IN_FLIGHT" || stitchBlocked.code === "MASTER_IN_FLIGHT"
                  ? "Already working on this book"
                  : "Stitch blocked — scope not fully rendered"}
              </Message.Header>
              <p>
                {stitchBlocked.message
                  || stitchBlocked.readiness?.blockers?.[0]?.message
                  || "Some in-scope lines are not rendered yet."}
              </p>
            </Message>
          )}
          {stitchReadiness?.status === "red" && (
            <Message warning size="small">
              <Message.Header>Not stitchable yet</Message.Header>
              <p>{stitchReadiness.blockers?.[0]?.message}</p>
              {stitchReadiness.chapters?.some((c) => c.missing > 0) && (
                <p style={{ marginTop: 4 }}>
                  Missing by chapter:{" "}
                  {stitchReadiness.chapters
                    .filter((c) => c.missing > 0)
                    .map((c) => `ch${c.chapterNumber ?? "—"} (${c.missing})`)
                    .join(", ")}
                </p>
              )}
            </Message>
          )}
          {/*
            ⭐ THE EXISTING STITCH IS OUT OF DATE. Until 2026-09-22 nothing in this app asked whether a
            chapter's stitch predated its audio, so re-rendering one line after stitching left the
            chapter quietly holding the old take — audible only in the finished file. Restoring a
            superseded take makes that cheap and frequent, so the gate now answers it and it is said
            HERE: only red blockers were displayed before, which would have left this amber verdict
            invisible.
          */}
          {stitchReadiness?.status !== "red" && stitchReadiness?.staleChapters?.length > 0 && (
            <Message warning size="small">
              <Message.Header>The existing stitch is older than the audio</Message.Header>
              <p>
                {stitchReadiness.warnings?.find((w) => w.code === "stitch-stale")?.message
                  || "Some chapters were stitched before the newest audio in scope."}
              </p>
              <p style={{ marginTop: 4 }}>
                {stitchReadiness.staleChapters
                  .map((c) => `ch${c.chapterNumber}${c.restored ? " (take restored)" : ""}`)
                  .join(", ")}
              </p>
            </Message>
          )}
          {stitchJob && (
            <Segment className="rs-stitch-job" style={{ marginTop: 4 }}>
              <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 8 }}>
                <Icon
                  className="loading-icon"
                  name={
                    stitchJob.status === "complete"
                      ? "check circle"
                      : stitchJob.status === "failed"
                        ? "times circle"
                        : "circle notched"
                  }
                  loading={stitchJobInFlight}
                  color={
                    stitchJob.status === "complete"
                      ? "green"
                      : stitchJob.status === "failed"
                        ? "red"
                        : "grey"
                  }
                />
                <strong>Stitch job</strong>
                <span style={{ color: "#888" }}>
                  {stitchJob.selector} · {stitchJob.totals?.done ?? 0}/
                  {stitchJob.totals?.chapters ?? 0} chapters
                  {stitchJob.totals?.failed ? ` · ${stitchJob.totals.failed} failed` : ""}
                  {/*
                    WHY A WHOLE-BOOK STITCH MAY SAY "1 chapter". A chapter file whose lines have not
                    changed since it was built is left alone, so changing one chapter costs one
                    chapter and a crashed run resumes. Without this line that reads as a failure.
                  */}
                  {!!stitchJob.chaptersSkipped && (
                    <> · {stitchJob.chaptersSkipped} already current, skipped</>
                  )}
                </span>
              </div>
              {/* This stitch is narrower than what has been rendered. A stitch cannot tell it
                  is incomplete on its own — it finds audio for every unit it asks about and
                  reports success — so the only chance to notice is here, while it runs. */}
              {stitchJob.status === "running" && (
                <Button
                  size="mini"
                  basic
                  color="red"
                  style={{ margin: "0.4em 0" }}
                  disabled={stitchJob.stopRequested}
                  onClick={() => dispatch(stopStitch(stitchJob.jobId))}
                  title="Finish the current chapter, then stop. Chapters already assembled are kept."
                >
                  <Icon name={stitchJob.stopRequested ? "hourglass half" : "stop"} />
                  {stitchJob.stopRequested ? "Stopping after this chapter…" : "Stop"}
                </Button>
              )}
              {stitchJob.warning && (
                <Message warning size="tiny" style={{ margin: "0.5em 0" }}>
                  <Icon name="warning sign" />
                  {stitchJob.warning}
                </Message>
              )}
              {(stitchJob.chapters || []).map((c) => (
                <div
                  key={c.chapterNumber}
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: 10,
                    padding: "4px 0",
                    borderTop: "1px solid #f0f0f0",
                    flexWrap: "wrap",
                  }}
                >
                  <Label size="small" color={c.status === "done" ? "green" : c.status === "failed" ? "red" : "grey"}>
                    Chapter {c.chapterNumber}
                  </Label>
                  <span style={{ fontSize: "0.85em", color: "#666", minWidth: 120 }}>
                    {c.status}
                    {c.status === "downloading" ? ` ${c.downloaded}/${c.lineCount}` : ""}
                    {c.durationMs != null
                      ? ` · ${(c.durationMs / 1000).toFixed(1)}s`
                      : ""}
                  </span>
                  {c.signedUrl && (
                    // eslint-disable-next-line jsx-a11y/media-has-caption
                    <audio controls preload="none" src={c.signedUrl} style={{ height: 32 }} />
                  )}
                  {c.status === "done" && c.renderedChapterUuid && (
                    <Button
                      size="mini"
                      basic
                      icon="refresh"
                      title="Refresh the audio link if playback fails (URLs expire hourly)"
                      onClick={() => dispatch(resignRenderedChapterUrl(c.renderedChapterUuid))}
                    />
                  )}
                  {c.error && (
                    <span style={{ color: "#c00", fontSize: "0.85em" }}>{c.error}</span>
                  )}
                </div>
              ))}
              {stitchJob.status === "complete" && stitchJob.totals?.failed === 0 && (
                <p style={{ marginTop: 8, marginBottom: 0, color: "#2a7" }}>
                  <Icon name="check" /> Raw chapter audio assembled. (ACX mastering —
                  loudnorm + head/tail room — is the next stage.)
                </p>
              )}
            </Segment>
          )}
            </>
          )}

        </Segment>

        {/* Text still worth fixing. Deliberately ABOVE the casting gate: it is the cheaper
            problem and the one that gets discovered latest if missed. */}
        {bookId && (
          <UnresolvedTextNotice
            review={review}
            onOpenIngestLab={() => navigate("/dashboard/book-creator/talent-agency")}
          />
        )}

        {/* Pre-render casting gate — is every speaker voiced? */}
        {bookId && <CoverageReport bookId={bookId} litGroupUuid={litGroup} />}

        {/* Two-column: tree (left) + detail (right). */}
        <div className={`rs-two-col${treeCollapsed ? " rs-tree-collapsed" : ""}`}>
          {/*
            ─── ⭐ GIVE THE TEXT COLUMN THE TREE'S WIDTH ────────────────────────────────────────────

            The operator's ask: let Render Units slide out of the way so the line table's Text column
            can gain 250–300px. It gains MORE than that — the tree is `minmax(300px, 360px)` plus a
            1rem gap, so collapsing to a 30px rail hands back ~316–376px.

            ⭐ AND IT ALL LANDS ON TEXT WITHOUT RETUNING A SINGLE COLUMN, because the line table is
            `table-layout: fixed` and `.rud-col-text` is the ONLY column with `width: auto` — every
            other one is a fixed pixel width. Text is also `white-space: normal`, so the gain shows up
            as shorter, fewer rows rather than merely longer lines.

            ⚠️ A COLLAPSE RATHER THAN A DRAG HANDLE, deliberately. The work is modal — pick a unit,
            then read the table for a long stretch — so the need is a boolean, not a continuous
            adjustment. A resize would want pointer capture, min/max clamping, touch handling and a
            persisted arbitrary number, for a want that has two states.

            ⚠️ AND A RAIL RATHER THAN ZERO. Collapsing to nothing loses the way back and the sense of
            which unit is open; the rail keeps both.
          */}
          {treeCollapsed && (
            <div className="rs-left-rail">
              <Popup
                size="tiny"
                content="Show the render-unit tree"
                trigger={
                  <Button
                    icon="angle double right"
                    size="mini"
                    basic
                    className="rs-rail-toggle"
                    aria-label="Show the render-unit tree"
                    onClick={() => setTreeCollapsed(false)}
                  />
                }
              />
              {/* Which unit is open, kept legible while the tree is away. */}
              {selectedNode?.title && (
                <div className="rs-rail-label" title={selectedNode.title}>{selectedNode.title}</div>
              )}
            </div>
          )}
          <div className="rs-left">
            {/* The collapse control lives on the tree's own header row, where its target is. */}
            <div className="rs-left-collapse">
              <Popup
                size="tiny"
                content="Hide the tree and widen the line text"
                trigger={
                  <Button
                    icon="angle double left"
                    size="mini"
                    basic
                    compact
                    aria-label="Hide the render-unit tree"
                    onClick={() => setTreeCollapsed(true)}
                  />
                }
              />
            </div>
            {/* ⛔ NO BOOK, NO TREE — and say so rather than showing an empty one.
                A blank tree is indistinguishable from a book that has no render units, and until
                2026-08-24 it was worse than blank: changing the LitGroup left the PREVIOUS book's
                tree on screen, fully interactive, beside a group selector naming somewhere else.
                Everything rendered from it belonged to a book the operator had not chosen. */}
            {!bookId ? (
              <Segment basic textAlign="center" className="rs-tree-empty">
                <Icon name="book" size="large" style={{ color: "#bbb" }} />
                <div style={{ marginTop: "0.5em", color: "#777", fontSize: "0.95em" }}>
                  {litGroup ? "Select a book to load its render units." : "Select a Literary Group, then a book."}
                </div>
              </Segment>
            ) : treeLoading && !tree ? (
              <Segment basic textAlign="center" className="rs-tree-loading">
                <Loader active inline="centered" size="small">
                  Loading render units…
                </Loader>
              </Segment>
            ) : (
              <RenderUnitTree
                tree={tree}
                selectedId={selectedId}
                onSelect={(node) => setSelectedId(node.nodeId)}
              />
            )}
          </div>
          <div className="rs-right">
            <RenderUnitDetail
              node={selectedNode}
              bookId={bookId}
              preflight={preflight}
              lineSet={lineSet}
              lineOutcomes={lineOutcomes}
              liveActions={liveActions}
              liveLineActions={liveLineActions}
              onRefreshUnit={refreshUnit}
              preflightLoading={preflightLoading}
              sequenceActions={sequenceActions}
              onGridComplete={handleGridComplete}
            />

            {/* Only the LINE SET is announced down here now: the preflight has its own placeholder
                in the banner above, and reporting it twice added a third element that appeared and
                vanished, moving everything below it each way. */}
            {lineRowsLoading && selectedNode && (
              <div className="rs-detail-fetching">
                <Loader active inline size="tiny" />{" "}
                <span>Fetching the line set…</span>
              </div>
            )}
          </div>
        </div>

        {/* ⭐ The change list, above the activity log and in the same register: a record to consult rather
            than a surface to work in. Part of the deliverable once a chain can remove or restore words. */}
        <ChangeListPanel changeList={changeList} loading={changeListLoading} error={changeListError} />

        {/* Activity log — collapsed by default. */}
        <ActivityLog entries={activity} />
      </Container>

      <LegacyStudioModal
        open={legacyModalOpen}
        onClose={() => setLegacyModalOpen(false)}
      />

      <Confirm
        open={confirmForce}
        onCancel={handleCancelForce}
        onConfirm={handleConfirmForce}
        header="Preflight blocked — force render?"
        content={confirmContent}
        confirmButton="Force render"
        cancelButton="Back off"
      />

      <Confirm
        open={confirmReRenderLines}
        onCancel={() => setConfirmReRenderLines(false)}
        onConfirm={handleConfirmReRenderLines}
        header="Re-render the selected lines?"
        content={
          <div className="content">
            <p>
              <strong>{selectedLineIds.size.toLocaleString()}</strong>{" "}
              line{selectedLineIds.size === 1 ? "" : "s"} will be generated again, replacing the
              audio they have now. Every other line in this unit is left untouched.
            </p>
            <p>
              This spends ElevenLabs credits for those lines only — which is the point: fixing one
              bad take should not cost a chapter.
            </p>
          </div>
        }
        confirmButton={`Re-render ${selectedLineIds.size} line${selectedLineIds.size === 1 ? "" : "s"}`}
        cancelButton="Cancel"
      />

      <Confirm
        open={confirmReRender}
        onCancel={() => setConfirmReRender(false)}
        onConfirm={handleConfirmReRender}
        header="Re-render this unit?"
        content={
          <div className="content">
            <p>
              Every line in <strong>{selectedNode?.label || selector}</strong> will be generated
              again from scratch{selectedNode?.counts?.lines
                ? <> — <strong>{selectedNode.counts.lines.toLocaleString()}</strong> lines</>
                : null}, including the ones that are already correct.
            </p>
            <p>
              This spends ElevenLabs credits. An ordinary <em>Render</em> reuses whatever is
              already rendered, so use this only when you need to replace a take.
            </p>
          </div>
        }
        confirmButton="Re-render everything"
        cancelButton="Cancel"
      />

      {confirmRestitch && (
        <ConfirmModal
          open={confirmRestitch}
          title="Rebuild every chapter?"
          icon="redo"
          danger={false}
          confirmLabel="Force re-stitch"
          cancelLabel="Cancel"
          note={
            "A chapter is judged current only against its lines' timestamps, so a change to the "
            + "stitch itself leaves every chapter looking up to date. Force is how you apply one."
          }
          onConfirm={() => { setConfirmRestitch(false); runStitch({ redo: true }); }}
          onCancel={() => setConfirmRestitch(false)}
        >
          <p>
            This re-assembles every chapter of <strong>{stitchScopeLabel}</strong>, even the ones
            reported as current — minutes of processing and a full re-upload.
          </p>
          <p style={{ marginBottom: 0 }}>
            It costs no ElevenLabs operations or characters: the rendered lines are reused. ⚠️ A new
            stitch supersedes the MASTERED chapters too, so re-master afterwards.
          </p>
        </ConfirmModal>
      )}
      {AuthStdFooter()}
    </Container>
  );
};

export default RenderingStudio;
